import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  MapPin,
  Calendar,
  Users,
  Star,
  CheckCircle,
  XCircle,
  FileText,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import MailIcon from "@mui/icons-material/Mail";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { KeyUri } from "../key";
import { jwtDecode } from "jwt-decode";
import { Rating, Box, Typography } from "@mui/material";
import { Plane, TrainFront, Bus, Truck } from "lucide-react";
import { Instagram, Linkedin, Youtube, Globe, Link } from "lucide-react";
import { trackEvent } from "../utils/metaPixel";
import SafeImage, { NoImage, pickImage, normalizeImages } from "../Global/SafeTmage";

function getMetaCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

function getFbc() {
  let fbc = getMetaCookie("_fbc");
  if (!fbc) {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get("fbclid");
    if (fbclid) {
      fbc = `fb.1.${Date.now()}.${fbclid}`;
      document.cookie = `_fbc=${fbc}; path=/; max-age=${90 * 24 * 60 * 60}`;
    }
  }
  return fbc;
}

const placeKey = (label) => (label || '').trim().toLowerCase();

const groupHotelsByPlace = (hotels) => {
  const groups = [];
  const keyToGroup = {};
  (hotels || []).forEach((h, idx) => {
    const label = (h.location_label || '').trim();
    const key = label ? placeKey(label) : `__blank_${idx}`;
    if (!keyToGroup[key]) {
      const group = { key, label: label || null, hotels: [] };
      keyToGroup[key] = group;
      groups.push(group);
    }
    keyToGroup[key].hotels.push(h);
  });
  return groups;
};

const isSameRoom = (a, b) =>
  !!a && !!b && a.room_type === b.room_type && a.room_name === b.room_name;

const buildStopGroups = (hotels) => {
  const placeGroups = groupHotelsByPlace(hotels);
  return placeGroups.map((pg) => {
    const hotelGroups = [];
    pg.hotels.forEach((hotel) => {
      const hKey = hotel.propertyId || hotel.hotel_name;
      const existing = hotelGroups.find((g) => g.key === hKey);
      if (existing) {
        (hotel.selected_rooms || []).forEach((sr) => {
          if (!existing.rooms.find((r) => isSameRoom(r, sr))) existing.rooms.push(sr);
        });
      } else {
        hotelGroups.push({
          key: hKey,
          hotel,
          rooms: hotel.property_type === 'existing'
            ? [...(hotel.selected_rooms || [])]
            : [{
                room_type: hotel.room_type || 'Standard',
                room_name: hotel.room_type || 'Standard',
                meal_plan: hotel.meal_plan || 'ep',
                price_per_night: hotel.price_per_night || 0,
                notes: hotel.notes || '',
              }],
        });
      }
    });
    return { ...pg, hotelGroups };
  });
};

const lightboxImg = {
  width: "auto", height: "auto", maxHeight: "75vh", maxWidth: "100%",
  minWidth: 200, minHeight: 120, objectFit: "contain", borderRadius: 3,
};

const instructorLinkIcons = {
  instagram: <Instagram size={15} />,
  linkedin: <Linkedin size={15} />,
  youtube: <Youtube size={15} />,
  website: <Globe size={15} />,
  pdf: <FileText size={15} />,
  other: <Link size={15} />,
};

/* Badges + title + meta row. `onImage` = white text version used on the desktop hero overlay. */
const HeroMeta = ({ program, avgRating, reviewCount, onImage = false }) => (
  <div className={onImage ? "text-white max-w-2xl" : "max-w-4xl"}>
    <div className="flex flex-wrap gap-2 mb-4">
      {program.category && (
        <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase shadow-sm">
          {program.category}
        </span>
      )}
      {program.duration && (
        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase ${
            onImage
              ? "bg-white/20 backdrop-blur-md text-white"
              : "bg-emerald-50 text-emerald-800 border border-emerald-100"
          }`}
        >
          {program.duration}
        </span>
      )}
    </div>

    <h1
      className={`font-serif font-bold leading-tight ${
        onImage
          ? "text-4xl lg:text-5xl text-white mb-3"
          : "text-3xl sm:text-4xl text-emerald-900 mb-4"
      }`}
    >
      {program.title}
    </h1>

    <div
      className={`flex flex-wrap gap-x-6 gap-y-2 text-sm ${
        onImage ? "text-slate-200" : "text-slate-600"
      }`}
    >
      {program.location?.city && (
        <div className="flex items-center gap-2">
          <MapPin size={18} className={onImage ? "text-emerald-300" : "text-emerald-600"} />
          <span>
            {program.location.city}
            {program.location.state ? `, ${program.location.state}` : ""}
          </span>
        </div>
      )}

      {program.date_type === "flexible" && program.show_slots_on_user_side !== false && (
        <div className="flex items-center gap-2">
          <Users size={18} className={onImage ? "text-emerald-300" : "text-emerald-600"} />
          <span>{program.available_slots || "Limited"} slots</span>
        </div>
      )}

      {avgRating > 0 && (
        <div className="flex items-center gap-2">
          <Star size={18} className="fill-yellow-400 text-yellow-400" />
          <span>
            {avgRating.toFixed(1)} ({reviewCount} reviews)
          </span>
        </div>
      )}
    </div>
  </div>
);

const WellnessDetailImageGallery = ({ images, title, overlay, fallback }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [openGallery, setOpenGallery] = useState(false);
  const allImages = useMemo(() => normalizeImages(images), [images]);

  if (allImages.length === 0) {
    return (
      <>
        <NoImage type="wellness" sx={{ height: { xs: 260, md: 500 }, borderRadius: 4 }} />
        {fallback && <div className="hidden md:block mt-6">{fallback}</div>}
      </>
    );
  }

  const prev = () => setSelectedImage((i) => (i - 1 + allImages.length) % allImages.length);
  const next = () => setSelectedImage((i) => (i + 1) % allImages.length);

  return (
    <>
      {/* Desktop hero (reference style) */}
      <div className="hidden md:block relative h-[500px] rounded-3xl overflow-hidden shadow-lg">
        <div className="absolute inset-0 cursor-pointer" onClick={() => setOpenGallery(true)}>
          <SafeImage src={allImages[selectedImage]} type="wellness" alt={title} />
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent pointer-events-none" />

        {allImages.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur rounded-full p-2 shadow-md hover:scale-110 transition"
            >
              <ChevronLeft className="w-5 h-5 text-gray-800" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur rounded-full p-2 shadow-md hover:scale-110 transition"
            >
              <ChevronRight className="w-5 h-5 text-gray-800" />
            </button>
            <button
              onClick={() => setOpenGallery(true)}
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur transition"
            >
              View all {allImages.length} photos
            </button>
          </>
        )}

        <div className="absolute inset-x-0 bottom-0 p-8 lg:p-10 text-white pointer-events-none">
          {overlay}
        </div>
      </div>

      {/* Mobile main image */}
      <div className="md:hidden relative w-full h-[300px] rounded-2xl overflow-hidden">
        <SafeImage src={allImages[selectedImage]} type="wellness" alt={title} />
        {allImages.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur rounded-full p-2 shadow-md active:scale-95"
            >
              <ChevronLeft className="w-5 h-5 text-gray-800" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur rounded-full p-2 shadow-md active:scale-95"
            >
              <ChevronRight className="w-5 h-5 text-gray-800" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
              {selectedImage + 1} / {allImages.length}
            </div>
          </>
        )}
      </div>

      {/* Mobile thumbnails */}
    {/* Thumbnails (mobile + desktop) */}
{allImages.length > 1 && (
  <div className="flex gap-2 md:gap-3 mt-2 md:mt-4 overflow-x-auto scrollbar-hide p-1">
    {allImages.map((img, idx) => (
      <div
        key={idx}
        onClick={() => setSelectedImage(idx)}
        className={`flex-shrink-0 w-[80px] h-[60px] md:w-[120px] md:h-[84px] rounded-lg md:rounded-xl overflow-hidden cursor-pointer transition-all duration-300
          ${selectedImage === idx
            ? 'ring-2 ring-emerald-500 scale-105'
            : 'ring-2 ring-transparent opacity-60 hover:opacity-100'}`}
      >
        <SafeImage src={img} type="wellness" alt={`${title} ${idx + 1}`} compact />
      </div>
    ))}
  </div>
)}

      {/* Lightbox */}
      {openGallery && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setOpenGallery(false)}
        >
          <button
            onClick={() => setOpenGallery(false)}
            className="absolute top-4 right-4 text-white bg-white/20 hover:bg-white/30 rounded-full p-2 z-10"
          >
            <XCircle className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/30 rounded-full p-3 z-10"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>

          <div onClick={(e) => e.stopPropagation()}>
            <SafeImage
              src={allImages[selectedImage]} type="wellness" alt={title}
              sx={{ ...lightboxImg, maxHeight: "90vh", maxWidth: "90vw" }}
            />
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/30 rounded-full p-3 z-10"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-sm font-medium bg-black/50 px-4 py-1.5 rounded-full">
            {selectedImage + 1} / {allImages.length}
          </div>
        </div>
      )}
    </>
  );
};

const amenityLabels = (room) => {
  const tags = [];
  if (room?.meal_plan && mealLabels[room.meal_plan] && room.meal_plan !== 'ep') {
    tags.push(mealLabels[room.meal_plan]);
  }
  return tags;
};

const AccommodationSummaryCard = ({ room, occupancy, allowNoRoom, onOpenPanel, onClear }) => {
  if (!room || !occupancy) {
    return (
      <div className="border border-dashed border-gray-300 rounded-2xl p-4 flex items-center justify-between bg-white">
        <p className="text-sm font-semibold text-gray-700">No room selected</p>
        <button onClick={onOpenPanel} className="text-sm font-semibold text-emerald-600 hover:text-emerald-700">
          Choose room
        </button>
      </div>
    );
  }

  const occLabel = occupancy.occupancy === 'shared' && occupancy.sharing_type
    ? sharingLabelsGlobal[occupancy.sharing_type] || occupancy.sharing_type
    : occupancy.occupancy.charAt(0).toUpperCase() + occupancy.occupancy.slice(1);

 return (
  <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white p-4 flex items-center gap-4">
   <ImageStrip variant="square" type="hotel" images={room.images} alt={room.type} />
    <div className="min-w-0 flex-1">
      <h3 className="font-bold text-gray-900 text-[15px] leading-snug">{room.type}</h3>
      <p className="text-sm text-gray-600 mt-1">{occLabel}</p>
    </div>

    <div className="flex items-center gap-3 flex-shrink-0">
      {allowNoRoom && (
        <span
          role="button" tabIndex={0}
          onClick={onClear}
          className="text-sm font-semibold text-gray-500 hover:text-gray-700"
        >
          Remove
        </span>
      )}
      <span className="w-px h-4 bg-gray-200" />
      <button onClick={onOpenPanel} className="text-sm font-semibold text-emerald-600 hover:text-emerald-700">
        Change
      </button>
    </div>
  </div>
);
};

const AccommodationOptionCard = ({ room, occupancy, isSelected, isBaseline, priceLabel, onSelect }) => {
  const occLabel = occupancy.occupancy === 'shared' && occupancy.sharing_type
    ? sharingLabelsGlobal[occupancy.sharing_type] || occupancy.sharing_type
    : occupancy.occupancy.charAt(0).toUpperCase() + occupancy.occupancy.slice(1);

  return (
    <div
      className={`rounded-2xl border-2 overflow-hidden bg-white transition-colors p-3
        ${isSelected ? 'border-emerald-500' : 'border-gray-200 hover:border-gray-300'}`}
    >
      <h4 className="font-bold text-gray-900 text-sm leading-snug">{room.type}</h4>
      <p className="text-xs text-gray-500 mt-1">{occLabel}</p>
      <p className={`text-xs font-semibold mt-1.5 ${isBaseline ? 'text-gray-500' : 'text-emerald-600'}`}>
        {priceLabel}
      </p>

      <button
        onClick={() => onSelect(room, occupancy)}
        disabled={isSelected}
        className={`mt-3 w-full text-xs font-bold py-2 rounded-lg transition-colors
          ${isSelected ? 'bg-emerald-100 text-emerald-400 cursor-default' : 'bg-slate-800 text-white hover:bg-slate-900'}`}
      >
        {isSelected ? 'Selected' : 'Select'}
      </button>
    </div>
  );
};

const ChangeAccommodationPanel = ({ accommodations, currentRoomIndex, currentOccIndex, onSelect, onClose }) => {
  const flatOptions = [];
  accommodations.forEach((room, rIdx) => {
    (room.occupancyOptions || []).forEach((occ, oIdx) => {
      flatOptions.push({ room, occupancy: occ, rIdx, oIdx });
    });
  });

  const baseline = flatOptions.length > 0
    ? Math.min(...flatOptions.map((o) => Number(o.occupancy.price || 0)))
    : 0;

  return (
    <div className="fixed inset-0 z-[999] flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full sm:max-w-xl h-full bg-white flex flex-col shadow-2xl">
        <div className="px-5 pt-5 pb-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Choose room</h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100">
            <XCircle size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-2 gap-3">
            {flatOptions.map(({ room, occupancy, rIdx, oIdx }) => {
              const isSelected = rIdx === currentRoomIndex && oIdx === currentOccIndex;
              const diff = Number(occupancy.price || 0) - baseline;
              const priceLabel = diff === 0
                ? `₹${Number(occupancy.price || 0).toLocaleString('en-IN')} · Base Price`
                : `+₹${diff.toLocaleString('en-IN')} for upgrade`;

              return (
                <AccommodationOptionCard
                  key={`${rIdx}-${oIdx}`}
                  room={room}
                  occupancy={occupancy}
                  isSelected={isSelected}
                  isBaseline={diff === 0}
                 // priceLabel={priceLabel}
                  onSelect={() => { onSelect(rIdx, oIdx); onClose(); }}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const HotelOptionCard = ({ hotel, room, isSelected, isBaseline, priceLabel, onSelect }) => (
  <div
    className={`rounded-2xl border-2 overflow-hidden bg-white transition-colors
      ${isSelected ? 'border-emerald-500' : 'border-gray-200 hover:border-gray-300'}`}
  >
   <div className="h-32 w-full overflow-hidden">
  <ImageStrip variant="slider" type="hotel" images={hotel.images} alt={hotel.hotel_name} />
</div>

    <div className="p-3">
      <h4 className="font-bold text-gray-900 text-sm leading-snug">
        {hotel.hotel_name}
        {hotel.star_rating ? ` · ${hotel.star_rating}★` : ''}
      </h4>

      <p className={`text-xs font-semibold mt-1 ${isBaseline ? 'text-gray-500' : 'text-emerald-600'}`}>
        {priceLabel}
      </p>

      {hotel.address && (
        <p className="text-[11px] text-gray-500 mt-1.5 flex items-start gap-1">
          <MapPin size={12} className="mt-0.5 flex-shrink-0" />
          {hotel.address}
        </p>
      )}

      <div className="flex flex-wrap gap-1.5 mt-2">
        {amenityLabels(room).map((tag) => (
          <span key={tag} className="text-[10px] font-medium bg-gray-100 text-gray-600 px-2 py-1 rounded-md">
            {tag}
          </span>
        ))}
      </div>

      <button
        onClick={() => onSelect(hotel, room)}
        disabled={isSelected}
        className={`mt-3 w-full text-xs font-bold py-2 rounded-lg transition-colors
          ${isSelected ? 'bg-emerald-100 text-emerald-400 cursor-default' : 'bg-slate-800 text-white hover:bg-slate-900'}`}
      >
        {isSelected ? 'Selected' : 'Select'}
      </button>
    </div>
  </div>
);

const TransportOptionCard = ({ t, isSelected, isBest, fits, totalGuests, onSelect }) => (
  <div
    className={`rounded-2xl border-2 overflow-hidden bg-white transition-colors relative
      ${!fits ? 'opacity-40 border-gray-200' : isSelected ? 'border-emerald-500' : 'border-gray-200 hover:border-gray-300'}`}
  >
    {isSelected && (
      <div className="absolute top-2 left-2 z-10 bg-emerald-600 text-white text-[10px] font-bold px-2 py-1 rounded-full">
        Selected
      </div>
    )}

   <div className="h-28 w-full overflow-hidden bg-gray-50">
  <ImageStrip variant="slider" type="transport" images={t.images} alt={t.vehicle} />
</div>

    <div className="p-3">
      <h4 className="font-bold text-gray-900 text-sm leading-snug">{t.vehicle}</h4>

      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {isBest && !isSelected && (
          <span className="text-[10px] font-semibold px-2 py-1 rounded-md bg-amber-50 text-amber-800 border border-amber-200">
            Recommended
          </span>
        )}
        {!fits && (
          <span className="text-[10px] font-semibold px-2 py-1 rounded-md bg-red-50 text-red-700 border border-red-200">
            Too small
          </span>
        )}
      </div>

      <p className="text-[11px] text-gray-500 mt-2 flex items-center gap-1">
        <Users size={12} />
        Up to {t.capacity} pax · {t.type === 'shared' ? 'Shared' : t.type === 'flight' ? 'Flight' : 'Private'}
        {t.type === 'flight' && t.flight_class ? ` · ${t.flight_class.replace('_', ' ')}` : ''}
      </p>

      {t.leg_label && <p className="text-[11px] text-gray-400 mt-1">{t.leg_label}</p>}
      {t.notes && <p className="text-[11px] text-gray-400 italic mt-1">{t.notes}</p>}
      {!fits && (
        <p className="text-[11px] text-red-400 mt-1">
          Seats {t.capacity} · not enough for {totalGuests} guests
        </p>
      )}

      <button
        onClick={() => { if (fits) onSelect(t); }}
        disabled={!fits || isSelected}
        className={`mt-3 w-full text-xs font-bold py-2 rounded-lg transition-colors
          ${isSelected ? 'bg-emerald-100 text-emerald-400 cursor-default'
            : fits ? 'bg-slate-800 text-white hover:bg-slate-900'
            : 'bg-gray-100 text-gray-300 cursor-not-allowed'}`}
      >
        {isSelected ? 'Selected' : fits ? 'Select' : 'N/A'}
      </button>
    </div>
  </div>
);

const ImageStrip = ({ images, alt, variant = "strip", type = "hotel" }) => {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [slide, setSlide] = useState(0);
  const imgs = normalizeImages(images);

  if (imgs.length === 0) {
    return (
      <div className={
        variant === "square" ? "w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 rounded-xl overflow-hidden"
        : variant === "slider" ? "w-full h-full"
        : "h-28 w-full rounded-xl overflow-hidden"}>
        <NoImage type={type} />
      </div>
    );
  }

  const SHOW = 4;
  const visible = imgs.slice(0, SHOW);
  const remaining = imgs.length - SHOW;
  const openAt = (i) => { setGalleryIndex(i); setGalleryOpen(true); };
  const go = (e, dir) => {
    e.stopPropagation();
    setSlide((s) => (s + dir + imgs.length) % imgs.length);
  };

  // image + arrows + counter, shared by the "square" and "slider" variants
  const slider = (
    <>
      <SafeImage src={imgs[slide]} type={type} alt={`${alt}-${slide}`} className="hover:opacity-90 transition" />
      {imgs.length > 1 && (
        <>
          <button type="button" onClick={(e) => go(e, -1)} aria-label="Previous image"
            className="absolute left-1.5 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-1 shadow hover:scale-110 transition">
            <ChevronLeft className="w-4 h-4 text-gray-800" />
          </button>
          <button type="button" onClick={(e) => go(e, 1)} aria-label="Next image"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-1 shadow hover:scale-110 transition">
            <ChevronRight className="w-4 h-4 text-gray-800" />
          </button>
          <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-md">
            {slide + 1} / {imgs.length}
          </span>
        </>
      )}
    </>
  );

  return (
    <>
      {variant === "square" ? (
        <div
          className="relative w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 rounded-xl overflow-hidden cursor-pointer"
          onClick={(e) => { e.stopPropagation(); openAt(slide); }}
        >
          {slider}
        </div>
      ) : variant === "slider" ? (
        <div
          className="relative w-full h-full overflow-hidden cursor-pointer"
          onClick={(e) => { e.stopPropagation(); openAt(slide); }}
        >
          {slider}
        </div>
      ) : (
        <div className="flex gap-1 h-28 overflow-hidden">
          {visible.map((src, i) => {
            const isLast = i === SHOW - 1 && remaining > 0;
            return (
              <div
                key={i}
                className={`relative overflow-hidden flex-shrink-0 cursor-pointer ${i === 0 ? "flex-1" : "w-[23%]"}`}
                onClick={(e) => { e.stopPropagation(); openAt(i); }}
              >
                <SafeImage src={src} type={type} alt={`${alt}-${i}`} className="hover:opacity-90 transition" />
                {isLast && (
                  <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center gap-1">
                    <span className="text-white text-xl font-bold">+{remaining}</span>
                    <span className="text-white text-xs">more</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {galleryOpen && (
        <div
          className="fixed inset-0 z-[99999] bg-black/95 flex items-center justify-center"
          onClick={() => setGalleryOpen(false)}
        >
          <button onClick={() => setGalleryOpen(false)}
            className="absolute top-4 right-4 text-white bg-white/20 hover:bg-white/30 rounded-full p-2 z-10">
            <XCircle className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setGalleryIndex((i) => (i - 1 + imgs.length) % imgs.length); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/30 rounded-full p-3 z-10">
            <ChevronLeft className="w-7 h-7" />
          </button>

          <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center gap-4 max-w-5xl w-full px-16">
            <SafeImage src={imgs[galleryIndex]} type={type} alt={alt} sx={lightboxImg} />
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide max-w-full">
              {imgs.map((src, i) => (
                <div key={i} onClick={() => setGalleryIndex(i)}
                  className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden cursor-pointer transition-all
                    ${galleryIndex === i ? "ring-2 ring-emerald-400 scale-105" : "opacity-60 hover:opacity-100"}`}>
                  <SafeImage src={src} type={type} alt={`${alt}-${i}`} compact />
                </div>
              ))}
            </div>
            <div className="text-white text-sm font-medium bg-black/50 px-4 py-1.5 rounded-full">
              {galleryIndex + 1} / {imgs.length}
            </div>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); setGalleryIndex((i) => (i + 1) % imgs.length); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/30 rounded-full p-3 z-10">
            <ChevronRight className="w-7 h-7" />
          </button>
        </div>
      )}
    </>
  );
};

const mealLabels = { ep: 'Room Only', cp: 'Breakfast included', map: 'Breakfast + Dinner', ap: 'All Meals' };

const sharingLabelsGlobal = {
  twin_sharing: "Twin (2 Pax)",
  triple_sharing: "Triple (3 Pax)",
  quad_sharing: "Quad (4 Pax)",
  multi_sharing: "Multi (5+ Pax)",
};

const HotelSummaryCard = ({ stop, selection, totalRooms, totalGuests, allowNoHotel, onOpenPanel, onClearHotel }) => {
  const [collapsed, setCollapsed] = useState(false);

  if (!selection) {
    return (
      <div className="border border-dashed border-gray-300 rounded-2xl p-4 flex items-center justify-between bg-white">
        <div>
          <p className="text-sm font-semibold text-gray-700">
            No hotel{stop.label ? ` in ${stop.label}` : ''}
          </p>
          <p className="text-xs text-gray-400">Arrange your own accommodation</p>
        </div>
        <button onClick={onOpenPanel} className="text-sm font-semibold text-emerald-600 hover:text-emerald-700">
          Add hotel
        </button>
      </div>
    );
  }

  const { hotel, room } = selection;
  const nights = Number(hotel.nights) || 1;

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100"
      >
        <div className="flex items-center gap-2.5 text-gray-900">
          <span className="text-sm font-semibold tracking-tight">
            Hotel · {nights} Night{nights > 1 ? 's' : ''}{stop.label ? ` · In ${stop.label}` : ''}
          </span>
        </div>
       <div className="flex items-center gap-3 flex-shrink-0">
          {allowNoHotel && (
            <span
              role="button" tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onClearHotel(); }}
              className="text-sm font-semibold text-gray-500 hover:text-gray-700"
            >
              Remove
            </span>
          )}
          <span className="w-px h-4 bg-gray-200" />
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onOpenPanel(); }}
            className="text-sm font-semibold text-orange-600 hover:text-orange-700"
          >
            Change
          </span>
        </div>
      </button>

{!collapsed && (
  <div className="p-4 flex items-start gap-4">
  <ImageStrip variant="square" type="hotel" images={hotel.images} alt={hotel.hotel_name} />

    <div className="min-w-0 flex-1">
      <div className="flex items-baseline gap-1.5 flex-wrap">
        <h3 className="font-bold text-gray-900 text-[15px] leading-snug">{hotel.hotel_name}</h3>
        {hotel.star_rating && (
          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
            {hotel.star_rating}★
          </span>
        )}
      </div>

      {hotel.address && <p className="text-xs text-gray-500 mt-1">📍 {hotel.address}</p>}

      <div className="mt-3">
        <p className="text-sm font-semibold text-gray-800">{room.room_name || room.room_type}</p>
        {room.meal_plan && (
          <p className="text-xs text-gray-500 mt-0.5">
            {mealLabels[room.meal_plan] || room.meal_plan}
          </p>
        )}
      </div>

      <button
        onClick={onOpenPanel}
        className="mt-2 text-xs font-semibold text-emerald-600 hover:text-emerald-700"
      >
        More room options
      </button>
    </div>
  </div>
)}
    </div>
  );
};

const ChangeHotelPanel = ({ stop, currentSelection, allowNoHotel, onSelect, onClearHotel, onClose }) => {
  const [query, setQuery] = useState('');
  const [starFilter, setStarFilter] = useState(null);
  const [sortBy, setSortBy] = useState('popularity');
  const [expandedHotelKey, setExpandedHotelKey] = useState(null); 

  const currentHotelName = currentSelection?.hotel?.hotel_name || null;
  const currentRoom = currentSelection?.room || null;

  const cheapestRoomOf = (rooms) => {
    if (!rooms || rooms.length === 0) return null;
    return [...rooms].sort((a, b) => Number(a.price_per_night || 0) - Number(b.price_per_night || 0))[0];
  };

  const baseline = (() => {
    let best = null;
    stop.hotelGroups.forEach(({ rooms }) => {
      const room = cheapestRoomOf(rooms);
      if (!room) return;
      const price = Number(room.price_per_night || 0);
      if (!best || price < best.price) best = { price };
    });
    return best?.price || 0;
  })();

  const filtered = React.useMemo(() => {
    let list = stop.hotelGroups.filter(({ hotel }) => {
      if (query && !hotel.hotel_name.toLowerCase().includes(query.toLowerCase())) return false;
      if (starFilter && Number(hotel.star_rating) < starFilter) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sortBy === 'price_low') {
        return Number(cheapestRoomOf(a.rooms)?.price_per_night || 0) - Number(cheapestRoomOf(b.rooms)?.price_per_night || 0);
      }
      return 0;
    });
    return list;
  }, [stop, query, starFilter, sortBy]);

  return (
    <div className="fixed inset-0 z-[999] flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full sm:max-w-xl h-full bg-white flex flex-col shadow-2xl">
        <div className="px-5 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              Change hotel{stop.label ? ` in ${stop.label}` : ''}
            </h2>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100">
              <XCircle size={20} className="text-gray-500" />
            </button>
          </div>

          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by hotel name"
            className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400"
          />

          <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
            {[3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setStarFilter(starFilter === n ? null : n)}
                className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors
                  ${starFilter === n ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >
                {n}★
              </button>
            ))}
          </div>
        </div>

        <div className="px-5 py-3 flex items-center justify-between text-xs text-gray-500 border-b border-gray-100">
          <span>Showing {filtered.length} stays{stop.label ? ` in ${stop.label}` : ''}</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-xs font-semibold text-gray-700 bg-transparent focus:outline-none"
          >
            <option value="popularity">Sort: Popularity</option>
            <option value="price_low">Sort: Price (low to high)</option>
          </select>
        </div>

    <div className="grid grid-cols-2 gap-3 mt-1">
  {filtered.flatMap((group) => {
    const { hotel, rooms } = group;
    if (!rooms || rooms.length === 0) return [];

    return rooms.map((room, ridx) => {
      const isThisRoomSelected =
        hotel.hotel_name === currentHotelName && isSameRoom(room, currentRoom);
      const diff = Number(room.price_per_night || 0) - baseline;
      const priceLabel = diff === 0
        ? `₹${Number(room.price_per_night || 0).toLocaleString('en-IN')} · Base Price`
        : `${diff > 0 ? '+' : '-'}₹${Math.abs(diff).toLocaleString('en-IN')}/night for upgrade`;

      return (
        <HotelOptionCard
          key={`${hotel.hotel_name}-${ridx}`}
          hotel={hotel}
          room={room}
          isSelected={isThisRoomSelected}
          isBaseline={diff === 0}
        //  priceLabel={priceLabel}
          onSelect={(h, r) => { onSelect(h, r); onClose(); }}
        />
      );
    });
  })}
</div>
      </div>
    </div>
  );
};

const vehicleIconFor = (vehicle = "") => {
  const v = vehicle.toLowerCase();
  if (v.includes("bus") || v.includes("tempo")) return "🚌";
  if (v.includes("suv") || v.includes("innova") || v.includes("ertiga")) return "🚙";
  return "🚗";
};

const priceOfTransport = (t) => {
  const usage = Number(t.usage_count) || 1;
  return (t.type === "shared" ? Number(t.price_per_person || 0) : Number(t.price_per_vehicle || 0)) * usage;
};

const TransportSummaryCard = ({ selection, autoSelected, allowNoTransport, onOpenPanel, onRemove }) => {
  const [collapsed, setCollapsed] = useState(false);

  if (!selection) {
    return (
      <div className="border border-dashed border-gray-300 rounded-2xl p-4 flex items-center justify-between bg-white">
        <div>
          <p className="text-sm font-semibold text-gray-700">Self-arranged transport</p>
          <p className="text-xs text-gray-400">Not included in package</p>
        </div>
        <button onClick={onOpenPanel} className="text-sm font-semibold text-emerald-600 hover:text-emerald-700">
          Add transfer
        </button>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100"
      >
        <div className="flex items-center gap-2.5 text-gray-900">
          <span className="text-lg leading-none">{vehicleIconFor(selection.vehicle)}</span>
          <span className="text-sm font-semibold tracking-tight">
            Transfer{selection.notes ? ` · ${selection.notes}` : ''}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {allowNoTransport && (
            <span
              role="button" tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              className="text-sm font-semibold text-gray-500 hover:text-gray-700"
            >
              Remove
            </span>
          )}
          <span className="w-px h-4 bg-gray-200" />
          <span
            role="button" tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onOpenPanel(); }}
            className="text-sm font-semibold text-emerald-600 hover:text-emerald-700"
          >
            Modify
          </span>
        </div>
      </button>

      {!collapsed && (
        <div className="p-4 flex items-start gap-4">
          <ImageStrip variant="square" type="transport" images={selection.images} alt={selection.vehicle} />

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <h3 className="font-bold text-gray-900 text-[15px] leading-snug">{selection.vehicle}</h3>
              {autoSelected && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                  Recommended
                </span>
              )}
            </div>

            {selection.notes && <p className="text-sm text-gray-600 mt-1">{selection.notes}</p>}

            <p className="flex items-center gap-1 mt-3 text-xs text-gray-500">
              <Users size={12} /> Up to {selection.capacity} pax · {selection.type === 'shared' ? 'Shared vehicle' : 'Private vehicle'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const TransportOptionsPanel = ({ transport, selectedTransport, totalGuests, onSelect, onClose }) => {
  const sorted = [...transport].sort((a, b) => Number(a.capacity || 0) - Number(b.capacity || 0));
  const fitting = transport.filter(t => {
    const cap = Number(t.capacity || 0);
    return cap === 0 || cap >= totalGuests;
  });
  const bestFit = [...fitting].sort((a, b) => priceOfTransport(a) - priceOfTransport(b))[0];

  return (
    <div className="fixed inset-0 z-[999] flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full sm:max-w-xl h-full bg-white flex flex-col shadow-2xl">
        <div className="px-5 pt-5 pb-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Transfer options</h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100">
            <XCircle size={20} className="text-gray-500" />
          </button>
        </div>

<div className="flex-1 overflow-y-auto px-5 py-4">
  <div className="grid grid-cols-2 gap-3">
    {sorted.map((t, idx) => {
      const capacity = Number(t.capacity || 0);
      const fits = capacity === 0 || capacity >= totalGuests;
      const isBest = fits && bestFit?.vehicle === t.vehicle && bestFit?.type === t.type;
      const isSel = selectedTransport?.vehicle === t.vehicle && selectedTransport?.type === t.type;

      return (
        <TransportOptionCard
          key={idx}
          t={t}
          isSelected={isSel}
          isBest={isBest}
          fits={fits}
          totalGuests={totalGuests}
          onSelect={(picked) => { onSelect(picked); onClose(); }}
        />
      );
    })}
  </div>
</div>
      </div>
    </div>
  );
};

const WellnessDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const bookNowRef = useRef(null);
  const [showSticky, setShowSticky] = useState(true);

  const scheduleIdFromState = location.state?.scheduleId;
  const [kidsCount, setKidsCount] = useState(0);
  // Contact form state
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [travelDate, setTravelDate] = useState("");
  const [travellerCount, setTravellerCount] = useState(1);
  const [message, setMessage] = useState("");
  const [userId, setUserId] = useState(null);

  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [serviceTerms, setServiceTerms] = useState("");
  const [errors, setErrors] = useState({});

  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState("");
  const [paymentOption, setPaymentOption] = useState("partial");
  const [paymentGateway, setPaymentGateway] = useState("razorpay");
  const [cashfreeLoading, setCashfreeLoading] = useState(false);
  const [selectedTransportIndex, setSelectedTransportIndex] = useState(null);
  const [showTransportList, setShowTransportList] = useState(false);
  const [advancePercentage, setAdvancePercentage] = useState(30);
  const [selectedHotelIndex, setSelectedHotelIndex] = useState(null);
  const [selectedHotelRoomIndex, setSelectedHotelRoomIndex] = useState(null);
  const [advanceBookingDays, setAdvanceBookingDays] = useState(10);
  const [checkoutEventId, setCheckoutEventId] = useState(null);
const [selectedHotels, setSelectedHotels] = useState({});
const [changeHotelStop, setChangeHotelStop] = useState(null);
const [transportAutoSelected, setTransportAutoSelected] = useState(false);
const [showAccommodationPanel, setShowAccommodationPanel] = useState(false);
const [stayChoice, setStayChoice] = useState(null); 

const [removedSightseeing, setRemovedSightseeing] = useState({});
const sightseeingKey = (dayIdx, itemIdx) => `${dayIdx}-${itemIdx}`;
const isItemRemovable = (item) => item?.type !== "activity" || item.removable !== false;

const isSightseeingRemoved = (dayIdx, itemIdx, item) =>
  isItemRemovable(item) && !!removedSightseeing[sightseeingKey(dayIdx, itemIdx)];

const toggleSightseeing = (dayIdx, itemIdx, item) => {
  if (!isItemRemovable(item)) return;
  if (!userId) {
    alert("Please log in to customise the activities in your program.");
    // or: navigate('/login') / open your login modal
    return;
  }
  setRemovedSightseeing((prev) => {
    const key = sightseeingKey(dayIdx, itemIdx);
    const next = { ...prev };
    if (next[key]) delete next[key];
    else next[key] = true;
    return next;
  });
};

  useEffect(() => {
    const tokenUser =
      localStorage.getItem("tokenUser") || localStorage.getItem("tokenuser");
    if (tokenUser) {
      try {
        const token = tokenUser.replace(/^"|"$/g, "");
        const decoded = jwtDecode(token);
        setUserId(decoded.id || decoded._id || decoded.userId);
      } catch (err) {}
    }
  }, []);

  useEffect(() => {
    const fetchServiceTerms = async () => {
      try {
        const res = await axios.get(
          `${KeyUri.BACKEND_URI}/terms/service-user-terms`,
        );
        setServiceTerms(res.data.serviceUserTerms || "");
      } catch (error) {
        console.error("Failed to fetch terms:", error);
      }
    };
    fetchServiceTerms();
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    script.async = true;
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);

  // Fetch advance percentage from TourPricing
  useEffect(() => {
    const fetchTourPricing = async () => {
      try {
        const res = await axios.get(`${KeyUri.BACKEND_URI}/tourpricing`);
        console.log("🔍 TourPricing full response:", res.data);
        const pricing = res.data?.data;
        console.log("🔍 Pricing object:", pricing);
        console.log("🔍 advance_percentage:", pricing?.advance_percentage);
        if (pricing?.advance_percentage) {
          setAdvancePercentage(Number(pricing.advance_percentage));
          setAdvanceBookingDays(Number(pricing?.advance_booking_days || 10));
        }
      } catch (err) {
        console.error("❌ Failed to fetch tour pricing:", err);
      }
    };
    fetchTourPricing();
  }, []);

  // Booking Modal state
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [bookingName, setBookingName] = useState("");
  const [bookingPhone, setBookingPhone] = useState("");
  const [bookingEmail, setBookingEmail] = useState("");
  const [bookingTravelDate, setBookingTravelDate] = useState("");
  const [bookingTravellerCount, setBookingTravellerCount] = useState(1);
  const [bookingMessage, setBookingMessage] = useState("");
  const [selectedRoomIndex, setSelectedRoomIndex] = useState(null);
  const [selectedOccupancyIndex, setSelectedOccupancyIndex] = useState(null);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);

  const canPayAdvance = React.useMemo(() => {
    const effectiveDate =
      program?.date_type === "fixed"
        ? selectedSchedule?.startDate
        : bookingTravelDate || travelDate;

    if (!effectiveDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tripDate = new Date(effectiveDate);
    tripDate.setHours(0, 0, 0, 0);

    const diffDays = Math.ceil((tripDate - today) / (1000 * 60 * 60 * 24));

    return diffDays > advanceBookingDays;
  }, [
    bookingTravelDate,
    travelDate,
    selectedSchedule,
    advanceBookingDays,
    program?.date_type,
  ]);

  useEffect(() => {
    if (!canPayAdvance) {
      setPaymentOption("full");
    }
  }, [canPayAdvance]);
  useEffect(() => {
    const fetchProgram = async () => {
      try {
        setLoading(true);
        const fbp = getMetaCookie("_fbp");
        const fbc = getFbc();

        const res = await axios.get(
          `${KeyUri.BACKEND_URI}/wellness-packages/${id}`,
          { params: { fbp, fbc } },
        );

        setProgram(res.data);

        trackEvent(
          "ViewContent",
          {
            content_ids: [res.data._id],
            content_type: "wellness_package",
            content_name: res.data.title,
            value: Number(res.data.program_only?.display_price || 0),
            currency: "INR",
          },
          res.data.viewEventId,
        );

        setReviews(res.data.reviews || []);
        setAvgRating(res.data.stats?.avg || 0);
        setReviewCount(res.data.stats?.count || 0);
      } catch (err) {
        console.error("Failed to load wellness program:", err);
        setProgram(null);
        setReviews([]);
        setAvgRating(0);
        setReviewCount(0);
      } finally {
        setLoading(false);
      }
    };
    fetchProgram();
  }, [id]);

  useEffect(() => {
    const fetchSchedules = async () => {
      if (!program?._id || program.date_type !== "fixed") return;

      try {
        setScheduleLoading(true);
        const res = await axios.get(
          `${KeyUri.BACKEND_URI}/retreat-schedules/package/${program._id}`,
        );

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const upcoming = (res.data || []).filter((item) => {
          const start = new Date(item.startDate);
          start.setHours(0, 0, 0, 0);
          return start >= today;
        });

        setSchedules(upcoming);
      } catch (err) {
        console.error("Failed to fetch schedules", err);
        setSchedules([]);
      } finally {
        setScheduleLoading(false);
      }
    };

    fetchSchedules();
  }, [program]);

  useEffect(() => {
    if (!scheduleIdFromState || schedules.length === 0) return;

    const matched = schedules.find(
      (s) => s._id.toString() === scheduleIdFromState.toString(),
    );

    if (matched) {
      setSelectedSchedule(matched);
    }
  }, [scheduleIdFromState, schedules]);

  useEffect(() => {
    setCouponDiscount(0);
    setCouponCode("");
    setCouponError("");
  }, [
    travelDate,
    selectedRoomIndex,
    selectedOccupancyIndex,
    selectedHotels,
    changeHotelStop
  ]);

useEffect(() => {
  if (!program?.transport?.length) return;
  if (program.travel_policy === "not_required") return;

  const sharedOptions = program.transport
    .map((t, idx) => ({ ...t, idx }))
    .filter((t) => t.type === "shared");

  if (sharedOptions.length > 0) {
    const cheapest = sharedOptions.reduce((min, t) =>
      Number(t.price_per_person || 0) < Number(min.price_per_person || 0) ? t : min,
    );
    setSelectedTransportIndex(cheapest.idx);
    setTransportAutoSelected(true);
  } else {
    setSelectedTransportIndex(0);
    setTransportAutoSelected(true);
  }
}, [program]);

useEffect(() => {
  const hasAccommodations = program?.accommodations?.length > 0;
  const hasHotels = program?.hotels?.length > 0;

  if (!hasAccommodations && !hasHotels) return;
  if (program.stay_policy === "not_available") return;

  let bestRoom = null;
  if (hasAccommodations) {
    let lowestPrice = Infinity, lowestRoom = 0, lowestOcc = 0;
    program.accommodations.forEach((room, rIndex) => {
      room.occupancyOptions?.forEach((option, oIndex) => {
        const p = option.price || 0;
        if (p < lowestPrice) {
          lowestPrice = p; lowestRoom = rIndex; lowestOcc = oIndex;
        }
      });
    });
    bestRoom = { price: lowestPrice, rIndex: lowestRoom, oIndex: lowestOcc };
  }

  let bestHotelTotal = null;
  let bestHotelSelections = null;
  if (hasHotels) {
    const stops = buildStopGroups(program.hotels);
    const initial = {};
    let total = 0;
    stops.forEach((stop) => {
      let cheapest = null;
      stop.hotelGroups.forEach(({ hotel, rooms }) => {
        rooms.forEach((room) => {
          const price = Number(room.price_per_night || 0);
          if (!cheapest || price < Number(cheapest.room.price_per_night || 0)) {
            cheapest = { hotel, room };
          }
        });
      });
      if (cheapest) {
        initial[stop.key] = cheapest;
        total += Number(cheapest.room.price_per_night || 0) * (Number(cheapest.hotel.nights) || 1);
      }
    });
    bestHotelTotal = total;
    bestHotelSelections = initial;
  }

  const pickRoom = bestRoom && (bestHotelTotal === null || bestRoom.price <= bestHotelTotal);

  if (pickRoom && bestRoom) {
    setSelectedRoomIndex(bestRoom.rIndex);
    setSelectedOccupancyIndex(bestRoom.oIndex);
    setSelectedHotels({});
    setStayChoice('room');
  } else if (bestHotelSelections) {
    setSelectedHotels(bestHotelSelections);
    setSelectedRoomIndex(null);
    setSelectedOccupancyIndex(null);
    setStayChoice('hotel');
  }
  setSelectedHotelIndex(null);
  setSelectedHotelRoomIndex(null);
}, [program]);


  const fetchAvailableCoupons = async () => {
    try {
      setCouponLoading(true);

      const res = await axios.get(
        `${KeyUri.BACKEND_URI}/public/retreat-coupons`,
      );

      // Extract coupon array safely
      const coupons = res.data.coupons || res.data.data || res.data;

      if (!Array.isArray(coupons)) {
        console.error("Coupons response is not an array", coupons);
        setAvailableCoupons([]);
        setCouponModalOpen(true);
        return;
      }

      const filtered = coupons.filter((coupon) => {
        if (!coupon.retreatIds && !coupon.wellnessIds) return true;

        if (coupon.retreatIds?.includes(program._id)) return true;

        if (coupon.wellnessIds?.includes(program._id)) return true;

        return false;
      });

      setAvailableCoupons(filtered);
      setCouponModalOpen(true);
    } catch (err) {
      console.error("Failed to fetch coupons", err);
    } finally {
      setCouponLoading(false);
    }
  };

  // Add after fetchReviews useEffect
  useEffect(() => {
    const checkUserBookings = async () => {
      if (!userId) return;

      try {
        const res = await axios.get(
          `${KeyUri.BACKEND_URI}/wellness-booking/user/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("tokenUser") || localStorage.getItem("tokenuser")}`,
            },
          },
        );

        const bookingsArray = res.data.data || res.data || [];

        const eligibleBookings = bookingsArray.filter((booking) => {
          const programIdFromBooking =
            booking.packageId?._id || booking.packageId;

          const matchesProgram =
            programIdFromBooking?.toString() === id.toString();

          const isConfirmedOrCompleted = ["confirmed", "completed"].includes(
            booking.status,
          );

          const bookingDate =
            booking.preferredDate || booking.startDate || booking.date;

          const isPast = bookingDate && new Date(bookingDate) < new Date();

          return matchesProgram && isConfirmedOrCompleted && isPast;
        });
      } catch (err) {
        console.error(
          "❌ Wellness bookings check failed:",
          err.response?.status,
          err.message,
        );
      }
    };

    if (program && userId) checkUserBookings();
  }, [id, program, userId]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};

    // Name validation
    if (!name.trim()) {
      newErrors.name = "Name is required";
    } else if (!/^[A-Za-z\s]+$/.test(name)) {
      newErrors.name = "Only alphabets allowed";
    }

    // Phone validation
    if (!phone) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(phone)) {
      newErrors.phone = "Phone must be 10 digits";
    }

    // Email validation
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      newErrors.email = "Invalid email address";
    }

    // Travel date validation
    if (!travelDate) {
      newErrors.travelDate = "Travel date is required";
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selected = new Date(travelDate);
      if (selected < today) {
        newErrors.travelDate = "Cannot select past date";
      }
    }

    // Guests validation
    if (!travellerCount || travellerCount < 1) {
      newErrors.travellerCount = "Minimum 1 guest required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    try {
      const response = await axios.post(
        `${KeyUri.BACKEND_URI}/wellness-bookings`,
        {
          firstName: name.split(" ")[0] || "Guest",
          lastName: name.split(" ").slice(1).join(" ") || "User",
          email,
          phone,
          wellnessProgram: program._id.toString(),
          preferredDate: travelDate,
          guests: Number(travellerCount),
          message: message || "Wellness inquiry",
          fbp: getMetaCookie("_fbp"),   
      fbc: getFbc(), 
          ...(userId && { userId }),
        },
      );

      alert(
        "Thank you! Your inquiry has been sent. We'll contact you within 24 hours.",
      );
    trackEvent(
    "Lead",
    {
      content_name: "wellness_enquiry",
      content_category: program.title,
      value: 0,
      currency: "INR",
    },
    response.data.eventId 
  );

      setName("");
      setPhone("");
      setEmail("");
      setTravelDate("");
      setTravellerCount(1);
      setMessage("");
      setSubmitSuccess(true);
    } catch (error) {
      console.error("Inquiry error:", error.response?.data || error.message);
      alert(
        error.response?.data?.message || "Submission failed. Please try again.",
      );
    }
  };

  const openBookingModal = () => {
    if (!termsAccepted) {
      alert("Please accept Terms & Conditions first");
      setTermsModalOpen(true);
      return;
    }

  if (program?.date_type === "fixed" && !selectedSchedule) {
  alert("Please select an available retreat date");
  document.getElementById("booking-card")?.scrollIntoView({ behavior: "smooth" });
  return;
}

    if (program?.date_type === "flexible") {
      setBookingTravelDate(travelDate);
    } else {
      setBookingTravelDate(selectedSchedule.startDate);
    }

    setBookingTravellerCount(travellerCount);
    setBookingModalOpen(true);
  };

  const closeBookingModal = () => {
    setBookingModalOpen(false);
    setBookingName("");
    setBookingPhone("");
    setBookingEmail("");
    setBookingTravelDate("");
    setBookingTravellerCount(1);
    setTravellerCount(1);
    setBookingMessage("");
    setPaymentOption("partial");
    setCouponDiscount(0);
    setCouponCode("");
    setKidsCount(0);
    if (
      program?.travel_policy === "mandatory" &&
      program?.transport?.length > 0
    ) {
      const sharedOptions = program.transport
        .map((t, idx) => ({ ...t, idx }))
        .filter((t) => t.type === "shared");

      if (sharedOptions.length > 0) {
        const cheapest = sharedOptions.reduce((min, t) =>
          Number(t.price_per_person || 0) < Number(min.price_per_person || 0)
            ? t
            : min,
        );
        setSelectedTransportIndex(cheapest.idx);
      } else {
        setSelectedTransportIndex(0);
      }
    } else {
      setSelectedTransportIndex(null);
    }
  };

 useEffect(() => {
  const handleScroll = () => {
    const el = bookNowRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setShowSticky(!(r.top < window.innerHeight && r.bottom > 0));
  };
  window.addEventListener("scroll", handleScroll);
  handleScroll();
  return () => window.removeEventListener("scroll", handleScroll);
}, []);

  const applyCoupon = async () => {
    const rawToken =
      localStorage.getItem("tokenUser") ||
      localStorage.getItem("tokenuser") ||
      localStorage.getItem("token");

    const token = rawToken ? rawToken.replace(/^"|"$/g, "") : null;
    // console.log("token --->", token)
    if (!userId) {
      setCouponError("Please login to apply coupon");
      return;
    }
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }

    const effectiveDate =
      program?.date_type === "fixed"
        ? selectedSchedule?.startDate
        : bookingTravelDate || travelDate;

    if (!effectiveDate) {
      setCouponError("Please select retreat date first");
      return;
    }

    try {
      setCouponError("");

      const currentSubtotal = totalBasePricePerPerson * Number(travellerCount);
      const currentGstTotal = gstPerPerson * Number(travellerCount);
      const currentFinalTotal = currentSubtotal + currentGstTotal;

      const retreatLocation = `${program?.location?.city}, ${program?.location?.state}`;
      console.log("Sending location:", retreatLocation);
      const res = await axios.post(
        `${KeyUri.BACKEND_URI}/retreat-coupon/validate`,
        {
          coupon_code: couponCode.trim(),
          retreatId: program._id,
          bookingAmount: currentFinalTotal,
          sessionDate: effectiveDate,
          userId: userId,
          participants: Number(travellerCount),
          location: retreatLocation,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      console.log("Sending location:", retreatLocation);
      const { discount } = res.data;

      setCouponDiscount(discount);

      alert(`Coupon applied! You saved ₹${discount.toLocaleString("en-IN")}`);
    } catch (err) {
      console.error(
        "Coupon validation failed:",
        err.response?.data || err.message,
      );

      setCouponError(err.response?.data?.message || "Invalid coupon");
      setCouponDiscount(0);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!bookingName || !bookingPhone || !bookingEmail || !bookingTravelDate) {
      alert("Please fill all required fields");
      return;
    }

    try {
      const totalPrice = Math.round(finalTotalAfterCoupon || 0);
      const payableNow =
        paymentOption === "partial"
          ? Math.max(Math.round((totalPrice * advancePercentage) / 100), 1)
          : totalPrice;

      const balanceDue = Math.max(totalPrice - payableNow, 0);
      const amountPaise = payableNow * 100;

      if (!window.Razorpay) {
        alert("Razorpay SDK not loaded");
        return;
      }

      const selectedRoom = program?.accommodations?.[selectedRoomIndex];

      const selectedOccupancy =
        selectedRoom?.occupancyOptions?.[selectedOccupancyIndex];
      const selectedTransport =
        program?.transport?.[selectedTransportIndex] || null;

      const nameParts = bookingName.trim().split(" ");
      const firstName = nameParts[0] || bookingName;
      const lastName = nameParts.slice(1).join(" ") || "";

      const bookingPayload = {
        // Package info
        packageId: program._id,
        packageTitle: program.title,
        duration: program.duration,
        eventId: checkoutEventId,

        // Customer info (provide both formats for compatibility)
        firstName: firstName,
        lastName: lastName,
        name: bookingName,
        email: bookingEmail,
        phone: bookingPhone,

        // Booking dates
        startDate: bookingTravelDate,
        preferredDate: bookingTravelDate,

        // Guest count (both formats)
        numberOfGuests: Number(travellerCount),
        guests: Number(travellerCount),
        kidsCount: Number(kidsCount),
        kidsPricePerPerson: kidsPricePerPerson,
        // Additional info
        specialRequirements: bookingMessage,
        specialRequests: bookingMessage,

        // Payment info
        totalPrice: totalPrice,
        amountPaid: 0,
        advanceAmount: payableNow,
        remainingAmount: balanceDue,

        paymentStatus: "pending",
        status: "pending",
        paymentType: paymentOption,

        dateType: program.date_type,
        scheduleId:
          program.date_type === "fixed" ? selectedSchedule?._id : null,
        bookingType: program.date_type === "fixed" ? "fixed" : "flexible",
        accommodation:
          selectedRoom && selectedOccupancy
            ? {
                type: selectedRoom.type,
                occupancy: selectedOccupancy.occupancy,
                pricePerPerson: selectedOccupancy.display_price || 0,
                originalPrice: selectedOccupancy.original_price || 0,
                gstRate: selectedOccupancy?.gst_rate || 0,
              }
            : null,
    transport: selectedTransport
  ? {
      vehicle: selectedTransport.vehicle,
      type: selectedTransport.type,
      price_per_person: selectedTransport.type === "shared" ? selectedTransport.price_per_person : 0,
      price_per_vehicle: selectedTransport.type === "private" ? selectedTransport.price_per_vehicle : 0,
      usage_count: Number(selectedTransport.usage_count) || 1,
    }
  : null,
      hotels: stopGroups
  .map((stop) => {
    const sel = selectedHotels[stop.key];
    if (!sel) return null;
    return {
      location_label: stop.label,
      hotel_name: sel.hotel.hotel_name,
      property_type: sel.hotel.property_type,
      propertyId: sel.hotel.propertyId || null,
      room_type: sel.room.room_type || "",
      room_name: sel.room.room_name || "",
      meal_plan: sel.room.meal_plan || "",
      price_per_night: Number(sel.room.price_per_night || 0),
      nights: Number(sel.hotel.nights) || 1,
      total_hotel_price:
        Number(sel.room.price_per_night || 0) * (Number(sel.hotel.nights) || 1),
    };
  })
  .filter(Boolean),
       selected_sightseeing: selectedSightseeingItems.map((item) => ({
          type:        item.type,
          place_id:    item.place_id || null,
          activity_id: item.activity_id || null,
          name:        item.name || "",
          location:    item.location || "",
          image:       item.image || "",
          price:       Number(item.price) || 0,
        })),
        sightseeing_addon_total: Number(sightseeingAddonTotal) || 0,

           fbc: getFbc(),               
  fbp: getMetaCookie("_fbp"),
        ...(userId && { userId }),
      };

      const res = await axios.post(
        `${KeyUri.BACKEND_URI}/wellness-booking`,
        bookingPayload,
        { headers: { "Content-Type": "application/json" } },
      );

      const booking = res.data?.booking || res.data;

      const bookingId = booking?._id;
      const bookingReference = booking?.bookingReference;

      if (!bookingId) {
        throw new Error("Booking creation failed");
      }
      const orderRes = await axios.post(
        `${KeyUri.BACKEND_URI}/wellness-booking/razorpay/create-order`,
        { bookingId },
        { headers: { "Content-Type": "application/json" } },
      );

      const { orderId, amount, currency } = orderRes.data;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount,
        currency: currency,
        order_id: orderId,
        name: program.title || "Wellness Booking",
        description: program.category || "Wellness Program",
        image: program.image || "",
        prefill: {
          name: bookingName,
          email: bookingEmail,
          contact: bookingPhone,
        },
        notes: {
          wellness_program_id: program._id,
          paymentOption,
          totalPrice: String(totalPrice),
          payableNow: String(payableNow),
          remainingAmount: String(balanceDue),
        },
        theme: { color: "#047857" },
        handler: async function (response) {
          try {
            if (couponCode && couponDiscount > 0) {
              try {
                const rawToken =
                  localStorage.getItem("tokenUser") ||
                  localStorage.getItem("tokenuser") ||
                  localStorage.getItem("token");
                const token = rawToken ? rawToken.replace(/^"|"$/g, "") : null;
                const bookingId = booking?._id;

                const payload = {
                  coupon_code: couponCode.trim(),
                  bookingId: bookingId,
                  userId: userId,
                  totalAmount: subtotal + gstTotal,
                  sessionDate: bookingTravelDate,
                  retreatId: program._id.toString(),
                  bookingRef: bookingReference,
                  participants: Number(travellerCount),
                };

                const response = await axios.post(
                  `${KeyUri.BACKEND_URI}/retreat-coupon/apply`,
                  payload,
                  { headers: { Authorization: `Bearer ${token}` } },
                );
              } catch (err) {
                console.error("FULL ERROR:", {
                  status: err.response?.status,
                  data: err.response?.data,
                  message: err.message,
                  url: err.config?.url,
                });
                console.error(" ERROR RESPONSE DATA:", err.response?.data);
              }
            }

            if (bookingId) {
              try {
                await axios.put(
                  `${KeyUri.BACKEND_URI}/wellness-booking/${bookingId}/payment`,
                  {
                    paymentStatus:
                      paymentOption === "partial" ? "partially_paid" : "paid",
                    paymentId: response.razorpay_payment_id,
                    amountPaid: payableNow,
                    advanceAmount:
                      paymentOption === "partial" ? payableNow : totalPrice,
                    remainingAmount: balanceDue,
                  },
                  { headers: { "Content-Type": "application/json" } },
                );

                await axios.put(
                  `${KeyUri.BACKEND_URI}/wellness-booking/${bookingId}/status`,
                  { status: "confirmed" },
                  { headers: { "Content-Type": "application/json" } },
                );
                // console.log("Booking status updated to confirmed");
              } catch (updateErr) {
                console.error(
                  "Failed to update payment/booking status:",
                  updateErr,
                );
              }
            }

            alert(
              paymentOption === "partial"
                ? `Advance payment successful! Your booking reference is ${
                    bookingReference || bookingId
                  }. Remaining amount: ₹${balanceDue.toLocaleString("en-IN")}`
                : `Booking confirmed successfully! Your booking reference is ${
                    bookingReference || bookingId
                  }`,
            );

            trackEvent(
              "Purchase",
              {
                value: payableNow,
                currency: "INR",
                content_ids: [program._id],
                content_type: "wellness_package",
                content_name: program.title,
              },
              checkoutEventId,
            );

            closeBookingModal();

            // Navigate to invoice page
            if (bookingId) {
              navigate(`/wellness-invoice?bookingId=${bookingId}`);
            }
          } catch (err) {
            console.error("Booking save failed");

            console.error("Status:", err.response?.status);
            console.error("Data:", err.response?.data);
            console.error("Message:", err.message);

            alert(
              err.response?.data?.message ||
                "Payment succeeded, but booking could not be saved.Please contact support",
            );
          }
        },
        modal: {
          ondismiss: async () => {
            try {
              await axios.put(
                `${KeyUri.BACKEND_URI}/wellness-booking/${bookingId}/status`,
                {
                  status: "cancelled",
                },
              );
            } catch (e) {
              console.error(e);
            }
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Booking submission error:", error);
      alert("Unable to start payment. Please try again.");
    }
  };

  const handleCashfreePayment = async () => {
    if (!bookingName || !bookingPhone || !bookingEmail || !bookingTravelDate) {
      alert("Please fill all required fields");
      return;
    }

    setCashfreeLoading(true);

    try {
      const totalPrice = Math.round(finalTotalAfterCoupon || 0);
      const payableNow =
        paymentOption === "partial"
          ? Math.max(Math.round((totalPrice * advancePercentage) / 100), 1)
          : totalPrice;
      const balanceDue = Math.max(totalPrice - payableNow, 0);

      const nameParts = bookingName.trim().split(" ");
      const selectedRoom = program?.accommodations?.[selectedRoomIndex];
      const selectedOccupancy =
        selectedRoom?.occupancyOptions?.[selectedOccupancyIndex];
      const selectedTransport =
        program?.transport?.[selectedTransportIndex] || null;

      const bookingPayload = {
        packageId: program._id,
        packageTitle: program.title,
        duration: program.duration,
        eventId: checkoutEventId,
        firstName: nameParts[0] || bookingName,
        lastName: nameParts.slice(1).join(" ") || "",
        name: bookingName,
        email: bookingEmail,
        phone: bookingPhone,
        startDate: bookingTravelDate,
        preferredDate: bookingTravelDate,
        numberOfGuests: Number(travellerCount),
        guests: Number(travellerCount),
        kidsCount: Number(kidsCount),
        kidsPricePerPerson: kidsPricePerPerson,
        specialRequirements: bookingMessage,
        totalPrice,
        amountPaid: 0,
        advanceAmount: payableNow,
        remainingAmount: balanceDue,
        paymentType: paymentOption,
        paymentStatus: "pending",
        status: "pending",
        dateType: program.date_type,
        scheduleId:
          program.date_type === "fixed" ? selectedSchedule?._id : null,
        bookingType: program.date_type === "fixed" ? "fixed" : "flexible",
        accommodation:
          selectedRoom && selectedOccupancy
            ? {
                type: selectedRoom.type,
                occupancy: selectedOccupancy.occupancy,
                pricePerPerson: selectedOccupancy.display_price || 0,
                originalPrice: selectedOccupancy.original_price || 0,
                gstRate: selectedOccupancy?.gst_rate || 0,
              }
            : null,
       transport: selectedTransport
  ? {
      vehicle: selectedTransport.vehicle,
      type: selectedTransport.type,
      price_per_person: selectedTransport.type === "shared" ? selectedTransport.price_per_person : 0,
      price_per_vehicle: selectedTransport.type === "private" ? selectedTransport.price_per_vehicle : 0,
      usage_count: Number(selectedTransport.usage_count) || 1,
    }
  : null,
    hotels: stopGroups
  .map((stop) => {
    const sel = selectedHotels[stop.key];
    if (!sel) return null;
    return {
      location_label: stop.label,
      hotel_name: sel.hotel.hotel_name,
      property_type: sel.hotel.property_type,
      propertyId: sel.hotel.propertyId || null,
      room_type: sel.room.room_type || "",
      room_name: sel.room.room_name || "",
      meal_plan: sel.room.meal_plan || "",
      price_per_night: Number(sel.room.price_per_night || 0),
      nights: Number(sel.hotel.nights) || 1,
      total_hotel_price:
        Number(sel.room.price_per_night || 0) * (Number(sel.hotel.nights) || 1),
    };
  })
  .filter(Boolean),
           fbc: getFbc(),               
  fbp: getMetaCookie("_fbp"),
        ...(userId && { userId }),
      };

      const bookingRes = await axios.post(
        `${KeyUri.BACKEND_URI}/wellness-booking`,
        bookingPayload,
        { headers: { "Content-Type": "application/json" } },
      );

      const booking = bookingRes.data?.booking || bookingRes.data;
      const bookingId = booking?._id;

      if (!bookingId) throw new Error("Booking creation failed");

      sessionStorage.setItem("cashfree_booking_id", bookingId);

      const rawToken =
        localStorage.getItem("tokenUser") ||
        localStorage.getItem("tokenuser") ||
        localStorage.getItem("token");
      const token = rawToken ? rawToken.replace(/^"|"$/g, "") : null;

      const orderRes = await axios.post(
        `${KeyUri.BACKEND_URI}/wellness-booking/cashfree/create-order`,
        { bookingId },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        },
      );

      const { payment_session_id } = orderRes.data;
      if (!payment_session_id) throw new Error("Failed to get payment session");

      const cashfree = window.Cashfree({
        mode:
          process.env.REACT_APP_CASHFREE_ENV === "PRODUCTION"
            ? "production"
            : "sandbox",
      });

      cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: "_self",
      });

      const pollInterval = setInterval(async () => {
        try {
          const verifyRes = await axios.post(
            `${KeyUri.BACKEND_URI}/wellness-booking/cashfree/verify`,
            { orderId: orderRes.data.order_id },
          );

          if (verifyRes.data.success) {
            clearInterval(pollInterval);

            const confirmedBookingId = verifyRes.data.booking?._id || bookingId;
            const bookingReference = verifyRes.data.booking?.bookingReference;

            try {
              await axios.put(
                `${KeyUri.BACKEND_URI}/wellness-booking/${confirmedBookingId}/payment`,
                {
                  paymentStatus:
                    paymentOption === "partial" ? "partially_paid" : "paid",
                  amountPaid: payableNow,
                  advanceAmount: payableNow,
                  remainingAmount: balanceDue,
                },
                { headers: { "Content-Type": "application/json" } },
              );
              await axios.put(
                `${KeyUri.BACKEND_URI}/wellness-booking/${confirmedBookingId}/status`,
                { status: "confirmed" },
                { headers: { "Content-Type": "application/json" } },
              );
            } catch (updateErr) {
              console.error("Failed to update booking status:", updateErr);
            }

            try {
              await axios.post(`${KeyUri.BACKEND_URI}/confirm`, {
                booking_id: confirmedBookingId,
              });
            } catch (e) {
              console.error("Confirmation send failed:", e.message);
            }

            trackEvent(
              "Purchase",
              {
                value: payableNow,
                currency: "INR",
                content_ids: [program._id],
                content_type: "wellness_package",
                content_name: program.title,
              },
              checkoutEventId,
            );

            alert(
              paymentOption === "partial"
                ? `Advance payment successful! Your booking reference is ${
                    bookingReference || confirmedBookingId
                  }. Remaining amount: ₹${balanceDue.toLocaleString("en-IN")}`
                : `Booking confirmed successfully! Your booking reference is ${
                    bookingReference || confirmedBookingId
                  }`,
            );

            closeBookingModal();
            navigate(`/wellness-invoice?bookingId=${confirmedBookingId}`);
          }
        } catch (err) {}
      }, 3000);

      setTimeout(() => clearInterval(pollInterval), 600000);
    } catch (err) {
      console.error("Cashfree payment error:", err);
      alert(
        err?.response?.data?.message ||
          "Payment initiation failed. Please try again.",
      );
    } finally {
      setCashfreeLoading(false);
    }
  };

    const handleHdfcPayment = async () => {
    if (!bookingName || !bookingPhone || !bookingEmail || !bookingTravelDate) {
      alert("Please fill all required fields");
      return;
    }
    setCashfreeLoading(true);
    try {
      const totalPrice = Math.round(finalTotalAfterCoupon || 0);
      const payableNow =
        paymentOption === "partial"
          ? Math.max(Math.round((totalPrice * advancePercentage) / 100), 1)
          : totalPrice;
      const balanceDue = Math.max(totalPrice - payableNow, 0);

      const nameParts = bookingName.trim().split(" ");
      const selectedRoom = program?.accommodations?.[selectedRoomIndex];
      const selectedOccupancy = selectedRoom?.occupancyOptions?.[selectedOccupancyIndex];
      const selectedTransport = program?.transport?.[selectedTransportIndex] || null;

      const bookingPayload = {
        packageId: program._id,
        packageTitle: program.title,
        duration: program.duration,
        eventId: checkoutEventId,
        firstName: nameParts[0] || bookingName,
        lastName: nameParts.slice(1).join(" ") || "",
        name: bookingName,
        email: bookingEmail,
        phone: bookingPhone,
        startDate: bookingTravelDate,
        preferredDate: bookingTravelDate,
        numberOfGuests: Number(travellerCount),
        guests: Number(travellerCount),
        kidsCount: Number(kidsCount),
        kidsPricePerPerson: kidsPricePerPerson,
        specialRequirements: bookingMessage,
        totalPrice,
        amountPaid: 0,
        advanceAmount: payableNow,
        remainingAmount: balanceDue,
        paymentType: paymentOption,
        paymentStatus: "pending",      // stays pending until HDFC verify confirms it
        status: "pending",
        dateType: program.date_type,
        scheduleId: program.date_type === "fixed" ? selectedSchedule?._id : null,
        bookingType: program.date_type === "fixed" ? "fixed" : "flexible",
        accommodation:
          selectedRoom && selectedOccupancy
            ? {
                type: selectedRoom.type,
                occupancy: selectedOccupancy.occupancy,
                pricePerPerson: selectedOccupancy.display_price || 0,
                originalPrice: selectedOccupancy.original_price || 0,
                gstRate: selectedOccupancy?.gst_rate || 0,
              }
            : null,
        transport: selectedTransport
          ? {
              vehicle: selectedTransport.vehicle,
              type: selectedTransport.type,
              price_per_person: selectedTransport.type === "shared" ? selectedTransport.price_per_person : 0,
              price_per_vehicle: selectedTransport.type === "private" ? selectedTransport.price_per_vehicle : 0,
              usage_count: Number(selectedTransport.usage_count) || 1,
            }
          : null,
        hotels: stopGroups
          .map((stop) => {
            const sel = selectedHotels[stop.key];
            if (!sel) return null;
            return {
              location_label: stop.label,
              hotel_name: sel.hotel.hotel_name,
              property_type: sel.hotel.property_type,
              propertyId: sel.hotel.propertyId || null,
              room_type: sel.room.room_type || "",
              room_name: sel.room.room_name || "",
              meal_plan: sel.room.meal_plan || "",
              price_per_night: Number(sel.room.price_per_night || 0),
              nights: Number(sel.hotel.nights) || 1,
              total_hotel_price:
                Number(sel.room.price_per_night || 0) * (Number(sel.hotel.nights) || 1),
            };
          })
          .filter(Boolean),
        selected_sightseeing: selectedSightseeingItems.map((item) => ({
          type: item.type,
          place_id: item.place_id || null,
          activity_id: item.activity_id || null,
          name: item.name || "",
          location: item.location || "",
          image: item.image || "",
          price: Number(item.price) || 0,
        })),
        sightseeing_addon_total: Number(sightseeingAddonTotal) || 0,
        fbc: getFbc(),
        fbp: getMetaCookie("_fbp"),
        ...(userId && { userId }),
      };

      const bookingRes = await axios.post(
        `${KeyUri.BACKEND_URI}/wellness-booking`,
        bookingPayload,
        { headers: { "Content-Type": "application/json" } },
      );

      const booking = bookingRes.data?.booking || bookingRes.data;
      const bookingId = booking?._id;
      if (!bookingId) throw new Error("Booking creation failed");

      const orderRes = await axios.post(
        `${KeyUri.BACKEND_URI}/payments/hdfc/create-order`,
        { type: "wellness", bookingId },
        { headers: { "Content-Type": "application/json" } },
      );

      const { payment_url } = orderRes.data;
      if (!payment_url) throw new Error("HDFC did not return a payment URL");

      window.location.href = payment_url; // leaves the React app
    } catch (err) {
            setCashfreeLoading(false);
      console.error("HDFC wellness payment error:", err);
            alert(err?.response?.data?.error || err?.response?.data?.message || "Payment initiation failed. Please try again.");
    }
  };

  const allImages = React.useMemo(() => {
    const imgArray = [];

    if (Array.isArray(program?.image)) {
      program.image.forEach(
        (img) => img && !imgArray.includes(img) && imgArray.push(img),
      );
    } else if (program?.image) {
      imgArray.push(program.image);
    }

    if (program?.images?.length > 0) {
      program.images.forEach(
        (img) => img && !imgArray.includes(img) && imgArray.push(img),
      );
    }

    return imgArray.length > 0 ? imgArray : ["/placeholder-wellness.jpg"];
  }, [program?.image, program?.images]);

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );

  if (!program)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-xl text-gray-600">Wellness program not found</p>
      </div>
    );

  const getLowestOccupancy = () => {
    if (!program?.accommodations?.length) return null;

    let lowest = null;

    program.accommodations.forEach((room) => {
      room.occupancyOptions?.forEach((option) => {
        if (!lowest || option.display_price < lowest.display_price) {
          lowest = option;
        }
      });
    });

    return lowest;
  };

  // ================== PRICING LOGIC ==================

  const stayPolicy = program?.stay_policy || "optional";
  const sharingLabels = {
    twin_sharing: "Twin (2 Pax)",
    triple_sharing: "Triple (3 Pax)",
    quad_sharing: "Quad (4 Pax)",
    multi_sharing: "Multi (5+ Pax)",
  };

  // Program price (already includes hidden charges, NO GST)
  const programPrice = program?.program_only?.display_price || 0;

  const selectedOption =
    selectedRoomIndex !== null && selectedOccupancyIndex !== null
      ? program?.accommodations?.[selectedRoomIndex]?.occupancyOptions?.[
          selectedOccupancyIndex
        ]
      : null;

 const stopGroups = buildStopGroups(program?.hotels);

// Sum across every stop the customer has a hotel selected for — each
// stop's own `nights` drives its own cost, like TourDetail's hotelAddonTotal.
const hotelTotalPrice = stopGroups.reduce((sum, stop) => {
  const sel = selectedHotels[stop.key];
  if (!sel) return sum;
  const stopNights = Number(sel.hotel.nights) || 1;
  return sum + Number(sel.room.price_per_night || 0) * stopNights;
}, 0);

const hasAnyHotelSelected = Object.keys(selectedHotels).length > 0;

const stayPrice = (() => {
  if (stayChoice === 'room' && selectedOption) return selectedOption.price || 0;
  if (stayChoice === 'hotel' && hasAnyHotelSelected) return hotelTotalPrice;
  return 0;
})();

  // GST rate from backend
  const stayGstRate = program?.gst_rate || 0;

  const basePricePerPerson = Number(programPrice) + Number(stayPrice);
  const totalBasePricePerPerson = basePricePerPerson;

  const selectedTransportOption =
    program?.transport?.[selectedTransportIndex] || null;
const transportUsageCount = Number(selectedTransportOption?.usage_count) || 1;

const transportCostPerPerson =
  selectedTransportOption?.type === "shared"
    ? Number(selectedTransportOption.price_per_person || 0) * transportUsageCount
    : 0;
const transportCostPerVehicle =
  selectedTransportOption?.type === "private"
    ? Number(selectedTransportOption.price_per_vehicle || 0) * transportUsageCount
    : 0;
const transportPerPersonAddon =
  selectedTransportOption?.type === "shared"
    ? Number(selectedTransportOption.price_per_person || 0) * transportUsageCount
    : selectedTransportOption?.type === "private"
      ? Math.round(
          (Number(selectedTransportOption.price_per_vehicle || 0) * transportUsageCount) /
            Math.max(Number(travellerCount), 1),
        )
      : 0;

      const selectedSightseeingItems = (program.schedule || []).flatMap((day, dayIdx) =>
  (day.sightseeing || [])
    .map((item, itemIdx) => ({ ...item, dayIdx, itemIdx }))
    .filter((item) => !isSightseeingRemoved(item.dayIdx, item.itemIdx, item) && item.price > 0)
);

const sightseeingPerPersonAddon = selectedSightseeingItems.reduce(
  (sum, item) => sum + Number(item.price || 0),
  0
);
const sightseeingAddonTotal = sightseeingPerPersonAddon * Number(travellerCount);

 const effectivePricePerPerson = basePricePerPerson + transportPerPersonAddon + sightseeingPerPersonAddon;
  const kidsDiscountPct = program?.kids_discount_percentage || 0;
  const kidsPricePerPerson = Math.round(
    effectivePricePerPerson * (1 - kidsDiscountPct / 100),
  );
  const kidsSubtotal = kidsPricePerPerson * Number(kidsCount);

  const adultsSubtotal = effectivePricePerPerson * Number(travellerCount);
  const subtotal = adultsSubtotal + kidsSubtotal;
  const subtotalAfterCoupon = Math.max(subtotal - couponDiscount, 0);
  const gstTotal = Math.round(
    (subtotalAfterCoupon * Number(stayGstRate)) / 100,
  );
  const gstPerPerson = Math.round(
    (totalBasePricePerPerson * Number(stayGstRate)) / 100,
  );
  const finalTotal =
    subtotal + Math.round((subtotal * Number(stayGstRate)) / 100);
  const finalTotalAfterCoupon = subtotalAfterCoupon + gstTotal;
  const totalBookingAmount = Math.round(finalTotalAfterCoupon || 0);

  const previewAdvanceAmount = Math.max(
    Math.round((totalBookingAmount * advancePercentage) / 100),
    1,
  );
  const previewRemainingAmount = Math.max(
    totalBookingAmount - previewAdvanceAmount,
    0,
  );

  // Actual amount that will be charged now, based on the selected option.
  const advanceAmount =
    paymentOption === "partial" ? previewAdvanceAmount : totalBookingAmount;

  const remainingAmount = Math.max(totalBookingAmount - advanceAmount, 0);
// original program price
const originalProgramPrice =
  program?.program_only?.original_display_price ||
  program?.original_base_program_price ||
  0;

// original stay price (only rooms carry an original price; hotels have no discount)
let originalStayPrice = 0;
if (stayChoice === "room" && selectedOption) {
  originalStayPrice = Number(selectedOption.original_price || selectedOption.price || 0);
} else if (stayChoice === "hotel" && hasAnyHotelSelected) {
  originalStayPrice = hotelTotalPrice; // no discount on hotels
}

// add-ons are not discounted, so they go on BOTH sides
const originalPricePerPerson =
  Number(originalProgramPrice) +
  originalStayPrice +
  transportPerPersonAddon +
  sightseeingPerPersonAddon;

const saveAmount = Math.max(originalPricePerPerson - effectivePricePerPerson, 0);
const hasDiscount = saveAmount > 0;
const savePercent =
  originalPricePerPerson > 0
    ? Math.round((saveAmount / originalPricePerPerson) * 100)
    : 0;


  const hasMultipleImages = allImages.length > 1;

  return (
    <div className="min-h-screen bg-slate-50 pb-24 lg:pb-0 overflow-x-clip">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-2 md:ml-8 pt-24 md:pt-24">
        <button
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate("/wellness");
            }
          }}
          className="flex items-center gap-2 text-emerald-700 font-medium hover:text-emerald-900 transition"
        >
          ← Back
        </button>
      </div>

      {/* Main layout: left = story (hero image first), right = booking card beside the image */}
<div className="max-w-7xl mx-auto px-4 md:px-8 pt-4 pb-8 mt-2">
  <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 min-w-0">
    <div className="lg:col-span-7 xl:col-span-8 space-y-8 lg:space-y-12 min-w-0">
            {/* 1. HERO — image card (desktop shows title on the image, mobile unchanged) */}
            <section id="hero">
              <WellnessDetailImageGallery
                images={program.image || program.images}
                title={program.title}
                overlay={
                  <HeroMeta
                    program={program}
                    avgRating={avgRating}
                    reviewCount={reviewCount}
                    onImage
                  />
                }
                fallback={
                  <HeroMeta
                    program={program}
                    avgRating={avgRating}
                    reviewCount={reviewCount}
                  />
                }
              />

              {/* Mobile-only title block below the image */}
              <div className="md:hidden mt-6">
                <HeroMeta
                  program={program}
                  avgRating={avgRating}
                  reviewCount={reviewCount}
                />
              </div>
            </section>

            {/* 2. About This Program */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 lg:p-10">
              <h2 className="text-2xl lg:text-3xl font-serif font-bold mb-6 text-emerald-900">
                About This Program
              </h2>
              <div
                className="text-gray-700 leading-relaxed prose max-w-none"
                dangerouslySetInnerHTML={{ __html: program.description || "" }}
              />
            </div>

            {/* 3. Benefits (The Transformation) */}
            {program.benefits && program.benefits.length > 0 && (
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 lg:p-10">
                <h2 className="text-2xl lg:text-3xl font-serif font-bold mb-8 text-emerald-900">
                  What You'll Experience
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8">
                  {program.benefits.map((benefit, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shadow-sm">
                        <CheckCircle size={22} />
                      </div>
                      <p className="text-slate-700 text-sm leading-relaxed self-center">
                        {benefit}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 4. Program Schedule (Itinerary) */}
            {program.schedule && program.schedule.length > 0 && (
              <section id="itinerary">
                <h2 className="text-2xl lg:text-3xl font-serif font-bold mb-8 text-emerald-900">
                  Program Schedule
                </h2>
                <div className="space-y-6">
                  {program.schedule.map((day, idx) => {
                    const dayNumber =
                      day.day?.replace(/^Day\s*/i, "") || idx + 1;

                    return (
                      <details
                        key={idx}
                        open={idx === 0}
                        className="group bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <summary className="flex items-center justify-between gap-4 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                          <div className="flex items-center gap-4 min-w-0">
                         <div
  className="w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center font-serif font-bold text-lg
    bg-emerald-100 text-emerald-700 border border-emerald-200
    group-open:bg-emerald-600 group-open:text-white group-open:border-emerald-600
    transition-colors"
>
  {dayNumber}
</div>
                            <h3 className="font-bold text-lg text-slate-800">
                              {day.title}
                            </h3>
                          </div>
                          <svg
                            className="w-5 h-5 flex-shrink-0 text-slate-400 transition-transform group-open:rotate-180"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </summary>

                        <div className="mt-4 sm:pl-16 text-slate-600 text-sm leading-relaxed">
                          <p className="mb-3">{day.description}</p>

                          {day.activities && day.activities.length > 0 && (
                            <div>
                              <p className="font-semibold text-slate-800 mb-2">Activities:</p>
                              <ul className="list-disc list-inside space-y-1">
                                {day.activities.map((activity, aIdx) => (
                                  <li key={aIdx}>{activity}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {Array.isArray(day.sightseeing) && day.sightseeing.length > 0 && (
                            <div className="mt-4 pt-3 border-t border-gray-100">
                              <p className="font-semibold text-slate-800 mb-2">Places & activities included this day:</p>
                              <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
                                {day.sightseeing.map((item, itemIdx) => {
                                 const removed = isSightseeingRemoved(idx, itemIdx, item);
                                  return (
                                    <div
                                      key={itemIdx}
                                      className={`flex-shrink-0 w-40 rounded-xl border overflow-hidden transition-opacity ${
                                        removed ? "opacity-40 border-gray-200" : "border-emerald-200"
                                      }`}
                                    >
                                      <div className="h-20 w-full bg-gray-100">
                                        {item.image && (
                                          <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.style.display = "none"; }}
                                          />
                                        )}
                                      </div>
                                      <div className="p-2">
                                        <div className="flex items-start justify-between gap-1">
                                          <p className="text-xs font-semibold text-gray-900 leading-snug line-clamp-2">{item.name}</p>
                                       {isItemRemovable(item) ? (
  <button
    type="button"
    onClick={() => toggleSightseeing(idx, itemIdx, item)}
    className="flex-shrink-0 text-gray-400 hover:text-gray-700"
    title={removed ? "Add back" : "Remove"}
  >
    {removed ? "+" : "✕"}
  </button>
) : (
  <span className="flex-shrink-0 text-[9px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
    Included
  </span>
)}
                                        </div>
                                        <p className="text-[10px] text-gray-400 mt-0.5">
                                          {item.type === "activity" ? "Activity" : "Place"}
                                          {item.location ? ` · ${item.location}` : ""}
                                        </p>
                                        {item.price > 0 && (
                                          <p className={`text-xs font-semibold mt-1 ${removed ? "text-gray-400 line-through" : "text-emerald-600"}`}>
                                            +₹{Number(item.price).toLocaleString("en-IN")}/person
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </section>
            )}

            {/* 5. Meet the Expert */}
            {program.instructor_profile?.name && (
              <section id="expert" className="bg-emerald-900 rounded-3xl p-8 lg:p-10 text-white flex flex-col md:flex-row gap-8 items-center md:items-start shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-700/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />

                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-emerald-700 shadow-xl flex-shrink-0 relative z-10 bg-emerald-800 flex items-center justify-center">
                  {program.instructor_profile.photo ? (
                    <img
                      src={program.instructor_profile.photo}
                      alt={program.instructor_profile.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <span className="text-4xl font-serif font-bold text-emerald-100">
                      {program.instructor_profile.name.charAt(0)}
                    </span>
                  )}
                </div>

                <div className="relative z-10 text-center md:text-left min-w-0">
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-3">
                    <h2 className="text-2xl lg:text-3xl font-serif font-bold">
                      {program.instructor_profile.name}
                    </h2>
                    {program.instructor_profile.experience_years > 0 && (
                      <span className="bg-emerald-700/50 border border-emerald-500/30 px-3 py-1 rounded-full text-xs font-semibold tracking-wide text-emerald-100 uppercase self-center md:self-auto">
                        {program.instructor_profile.experience_years}+ Years Experience
                      </span>
                    )}
                  </div>

                  {program.instructor_profile.bio && (
                    <p className="text-emerald-100 mb-5 text-base lg:text-lg max-w-xl">
                      {program.instructor_profile.bio}
                    </p>
                  )}

                  {program.instructor_profile.certifications?.filter(Boolean).length > 0 && (
                    <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-4">
                      {program.instructor_profile.certifications
                        .filter(Boolean)
                        .map((cert, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 bg-white/10 border border-white/20 text-emerald-50 text-xs rounded-full font-medium"
                          >
                            {cert}
                          </span>
                        ))}
                    </div>
                  )}

                  {program.instructor_profile.specializations?.filter(Boolean).length > 0 && (
                    <div className="mb-5">
                      <p className="text-xs font-semibold text-emerald-300 uppercase tracking-wider mb-2">
                        Areas of Expertise
                      </p>
                      <div className="flex flex-wrap justify-center md:justify-start gap-2">
                        {program.instructor_profile.specializations
                          .filter(Boolean)
                          .map((spec, i) => (
                            <span
                              key={i}
                              className="px-3 py-1 bg-emerald-700/40 border border-emerald-500/30 text-emerald-100 text-xs rounded-full font-medium"
                            >
                              {spec}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}

                  {program.instructor_profile.links?.filter((l) => l.url).length > 0 && (
                    <div className="flex flex-wrap justify-center md:justify-start gap-3">
                      {program.instructor_profile.links
                        .filter((l) => l.url)
                        .map((link, i) => (
                          <a
                            key={i}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 transition rounded-xl text-sm font-semibold border border-white/20"
                          >
                            {instructorLinkIcons[link.type] || instructorLinkIcons.other}
                            {link.label ||
                              link.type.charAt(0).toUpperCase() + link.type.slice(1)}
                          </a>
                        ))}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* 6. Logistics — Room Options */}
            {program.stay_policy !== "not_available" && program.accommodations?.length > 0 && (
              <div id="logistics" className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 lg:p-10">
                <h2 className="text-2xl lg:text-3xl font-serif font-bold text-emerald-900 mb-4">Room Options</h2>
                <AccommodationSummaryCard
                  room={program.accommodations?.[selectedRoomIndex]}
                  occupancy={program.accommodations?.[selectedRoomIndex]?.occupancyOptions?.[selectedOccupancyIndex]}
                  allowNoRoom={program.stay_policy !== "mandatory"}
                  onOpenPanel={() => setShowAccommodationPanel(true)}
                  onClear={() => {
                    setSelectedRoomIndex(null);
                    setSelectedOccupancyIndex(null);
                    setStayChoice(null);
                  }}
                />
              </div>
            )}

            {/* Logistics — Choose Hotel */}
            {program.stay_policy !== "not_available" && stopGroups.length > 0 && (
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 lg:p-10">
                <h2 className="text-2xl lg:text-3xl font-serif font-bold text-emerald-900 mb-4">Choose Accommodation</h2>
                <div className="space-y-4">
                  {stopGroups.map((stop) => (
                    <div key={stop.key}>
                      {stop.label && <p className="text-sm font-semibold text-gray-700 mb-2">📍 {stop.label}</p>}
                      <HotelSummaryCard
                        stop={stop}
                        selection={selectedHotels[stop.key] || null}
                        totalRooms={1}
                        totalGuests={travellerCount}
                        allowNoHotel={program.stay_policy !== "mandatory"}
                        onOpenPanel={() => setChangeHotelStop(stop)}
                        onClearHotel={() => {
                          setSelectedHotels((prev) => {
                            const next = { ...prev };
                            delete next[stop.key];
                            return next;
                          });
                          setStayChoice(null);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Logistics — Transport Options */}
            {program.travel_policy &&
              program.travel_policy !== "not_required" &&
              program.transport?.length > 0 && (
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 lg:p-10">
                  <div className="flex items-center gap-3 mb-4">
                    <Truck className="text-emerald-600" size={24} />
                    <h2 className="text-2xl lg:text-3xl font-serif font-bold text-emerald-900">Transport Options</h2>
                  </div>

                  {program.travel_policy === "mandatory" && (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-4">
                      Transport is included as part of this program.
                    </p>
                  )}

                  <TransportSummaryCard
                    selection={selectedTransportOption}
                    autoSelected={transportAutoSelected}
                    allowNoTransport={program.travel_policy !== "mandatory"}
                    onOpenPanel={() => setShowTransportList(true)}
                    onRemove={() => {
                      setSelectedTransportIndex(null);
                      setTransportAutoSelected(false);
                    }}
                  />
                </div>
              )}

            {/* 7. Inclusions and Exclusions */}
            <div className="grid md:grid-cols-2 gap-6">
              {program.inclusions && program.inclusions.length > 0 && (
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 lg:p-10">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle className="text-green-600" size={24} />
                    <h2 className="text-xl font-serif font-bold text-emerald-900">
                      What's Included
                    </h2>
                  </div>
                  <ul className="space-y-3">
                    {program.inclusions.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle
                          className="text-green-500 flex-shrink-0 mt-1"
                          size={18}
                        />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {program.exclusions && program.exclusions.length > 0 && (
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 lg:p-10">
                  <div className="flex items-center gap-2 mb-4">
                    <XCircle className="text-red-600" size={24} />
                    <h2 className="text-xl font-serif font-bold text-emerald-900">
                      What's Not Included
                    </h2>
                  </div>
                  <ul className="space-y-3">
                    {program.exclusions.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <XCircle
                          className="text-red-500 flex-shrink-0 mt-1"
                          size={18}
                        />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* 8. Location */}
      {program.location && (
  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 lg:p-10">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <div className="flex items-center gap-3">
        <MapPin className="text-emerald-600" size={24} />
        <h2 className="text-2xl lg:text-3xl font-serif font-bold text-emerald-900">
          Location
        </h2>
      </div>

      {program.map_link && (
        <a
          href={program.map_link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition-colors w-full sm:w-auto"
        >
          <MapPin size={16} />
          Open in Maps
        </a>
      )}
    </div>

                <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                  <p className="text-lg font-semibold text-gray-900 mb-1">
                    {program.location.fullAddress}
                  </p>
                  <p className="text-emerald-700 font-medium">
                    {program.location.city}, {program.location.state} -{" "}
                    {program.location.pincode}
                  </p>
                </div>
              </div>
            )}

            {/* 9. How To Reach */}
            {program.how_to_reach && (
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 lg:p-10">
                <h2 className="text-2xl lg:text-3xl font-serif font-bold text-emerald-900 mb-6">
                  How To Reach
                </h2>

                {program.how_to_reach.overview && (
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {program.how_to_reach.overview}
                  </p>
                )}

                <div className="space-y-5">
                  {/* By Air */}
                  {program.how_to_reach.by_air?.airport_name && (
                    <div className="flex gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50">
                      <div className="h-11 w-11 flex items-center justify-center rounded-full bg-sky-100 text-sky-600 flex-shrink-0">
                        <Plane className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">
                          By Air
                        </p>
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">
                            {program.how_to_reach.by_air.airport_name}
                          </span>

                          {program.how_to_reach.by_air.airport_code && (
                            <span className="ml-1 text-gray-500">
                              ({program.how_to_reach.by_air.airport_code})
                            </span>
                          )}

                          {program.how_to_reach.by_air.distance_label && (
                            <span className="ml-2 text-emerald-700 font-medium">
                              — {program.how_to_reach.by_air.distance_label}
                            </span>
                          )}

                          {program.how_to_reach.by_air.distance_km != null && (
                            <span className="ml-2 text-gray-500">
                              ({program.how_to_reach.by_air.distance_km} km)
                            </span>
                          )}
                        </p>
                        {program.how_to_reach.by_air.instructions && (
                          <p className="text-sm text-gray-600 mt-1">
                            {program.how_to_reach.by_air.instructions}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* By Train */}
                  {program.how_to_reach.by_train?.station_name && (
                    <div className="flex gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50">
                      <div className="h-11 w-11 flex items-center justify-center rounded-full bg-violet-100 text-violet-600 flex-shrink-0">
                        <TrainFront className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">
                          By Train
                        </p>
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">
                            {program.how_to_reach.by_train.station_name}
                          </span>

                          {program.how_to_reach.by_train.station_code && (
                            <span className="ml-1 text-gray-500">
                              ({program.how_to_reach.by_train.station_code})
                            </span>
                          )}

                          {program.how_to_reach.by_train.distance_label && (
                            <span className="ml-2 text-emerald-700 font-medium">
                              — {program.how_to_reach.by_train.distance_label}
                            </span>
                          )}

                          {program.how_to_reach.by_train.distance_km !=
                            null && (
                            <span className="ml-2 text-gray-500">
                              ({program.how_to_reach.by_train.distance_km} km)
                            </span>
                          )}
                        </p>
                        {program.how_to_reach.by_train.instructions && (
                          <p className="text-sm text-gray-600 mt-1">
                            {program.how_to_reach.by_train.instructions}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* By Road */}
                  {(program.how_to_reach.by_road?.bus_stand ||
                    program.how_to_reach.by_road?.distance_label ||
                    program.how_to_reach.by_road?.instructions) && (
                    <div className="flex gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50">
                      <div className="h-11 w-11 flex items-center justify-center rounded-full bg-amber-100 text-amber-600 flex-shrink-0">
                        <Bus className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">
                          By Road
                        </p>
                        {program.how_to_reach.by_road.distance_label && (
                          <p className="text-sm text-emerald-700 font-medium">
                            {program.how_to_reach.by_road.distance_label}
                          </p>
                        )}
                        {program.how_to_reach.by_road.bus_stand && (
                          <p className="text-sm text-gray-700">
                            Nearest bus stand:{" "}
                            <span className="font-medium">
                              {program.how_to_reach.by_road.bus_stand}
                            </span>
                          </p>
                        )}
                        {program.how_to_reach.by_road.instructions && (
                          <p className="text-sm text-gray-600 mt-1">
                            {program.how_to_reach.by_road.instructions}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Local Transfer */}
                  {program.how_to_reach.local_transfer?.available && (
                    <div className="flex gap-4 p-4 rounded-xl border border-emerald-100 bg-emerald-50">
                      <div className="h-11 w-11 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0">
                        <Truck className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="font-semibold text-emerald-800 mb-1">
                          Local Transfer Available
                        </p>
                        {program.how_to_reach.local_transfer.details && (
                          <p className="text-sm text-emerald-700">
                            {program.how_to_reach.local_transfer.details}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ================= RIGHT COLUMN: booking card (sits beside the hero image on desktop) ================= */}
          <div id="booking-card" className="lg:col-span-5 xl:col-span-4 self-start lg:sticky lg:top-24 z-30 min-w-0 scroll-mt-24">
  <div className="bg-white rounded-3xl p-5 lg:p-8 shadow-[0_20px_50px_-12px_rgb(0,0,0,0.1)] border border-gray-200">
    {/* Price Section */}
              <div className="mb-6 pb-6 border-b border-gray-100">
                <p className="text-sm text-slate-500 font-medium mb-1">Starting from</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-serif font-bold text-emerald-900">
                    ₹{effectivePricePerPerson.toLocaleString("en-IN")}
                  </span>
                  <span className="text-sm text-slate-500">/person</span>
                </div>

                {hasDiscount && (
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    <span className="text-sm text-gray-400 line-through">
                      ₹{originalPricePerPerson.toLocaleString("en-IN")}
                    </span>
                    <span className="inline-block bg-green-50 text-green-700 px-2 py-1 rounded text-xs font-bold border border-green-200">
                      SAVE ₹{saveAmount.toLocaleString("en-IN")} ({savePercent}% OFF)
                    </span>
                  </div>
                )}
              </div>

              {/* Booking Form */}
              {program.date_type === "flexible" && (
                <TextField
                  label="Travel Date"
                  type="date"
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date().toISOString().split("T")[0] }}
                  value={travelDate}
                  onChange={(e) => setTravelDate(e.target.value)}
                  margin="normal"
                  size="small"
                />
              )}

              {program.date_type === "fixed" && (
                <div className="mb-5">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Available Dates
                  </label>

                  {scheduleLoading ? (
                    <p className="text-sm text-gray-500">Loading schedules...</p>
                  ) : schedules.length === 0 ? (
                    <p className="text-sm text-red-500">No upcoming batches available</p>
                  ) : (
                    <div className="space-y-2">
                      {schedules.map((item) => {
                        const remaining = item.totalSlots - item.bookedSlots;
                        const active = selectedSchedule?._id === item._id;
                        const isFull =
                          item.is_full === true || item.status === "sold";

                        return (
                          <div
                            key={item._id}
                            onClick={() => {
                              if (!isFull) setSelectedSchedule(item);
                            }}
                            className={`w-full border-2 rounded-xl p-3 flex justify-between items-center transition ${
                              isFull
                                ? "opacity-50 cursor-not-allowed border-gray-200"
                                : active
                                  ? "border-emerald-500 bg-emerald-50 cursor-pointer"
                                  : "border-gray-200 hover:border-emerald-300 cursor-pointer"
                            }`}
                          >
                            <div>
                              <p className="font-bold text-emerald-900 text-sm">
                                {new Date(item.startDate).toLocaleDateString("en-IN")} -{" "}
                                {new Date(item.endDate).toLocaleDateString("en-IN")}
                              </p>
                              {isFull ? (
                                <p className="text-xs text-red-600 font-semibold mt-0.5 uppercase">
                                  Full
                                </p>
                              ) : (
                                item.show_available_slots && (
                                  <p className="text-xs text-emerald-600 font-semibold mt-0.5 uppercase">
                                    {remaining} spots left
                                  </p>
                                )
                              )}
                            </div>
                            {active && (
                              <CheckCircle className="text-emerald-500 flex-shrink-0" size={20} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="border border-gray-200 rounded-2xl p-3 mt-2 mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Guests
                </p>

                {/* Adults */}
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Adults</p>
                    <p className="text-xs text-gray-400">Above 12 Years</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setTravellerCount(
                          Math.max(1, Number(travellerCount) - 1),
                        );
                        setCouponDiscount(0);
                        setCouponCode("");
                        setCouponError("");
                      }}
                      disabled={Number(travellerCount) <= 1}
                      className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-emerald-500 hover:text-emerald-500 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      −
                    </button>
                    <span className="w-6 text-center text-sm font-semibold">
                      {String(travellerCount).padStart(2, "0")}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setTravellerCount(Number(travellerCount) + 1);
                        setCouponDiscount(0);
                        setCouponCode("");
                        setCouponError("");
                      }}
                      className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-emerald-500 hover:text-emerald-500"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Kids */}
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Kids</p>
                    <p className="text-xs text-gray-400">Below 12 Years</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setKidsCount(Math.max(0, Number(kidsCount) - 1));
                        setCouponDiscount(0);
                        setCouponCode("");
                        setCouponError("");
                      }}
                      disabled={Number(kidsCount) <= 0}
                      className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-emerald-500 hover:text-emerald-500 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      −
                    </button>
                    <span className="w-6 text-center text-sm font-semibold">
                      {String(kidsCount).padStart(2, "0")}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setKidsCount(Number(kidsCount) + 1);
                        setCouponDiscount(0);
                        setCouponCode("");
                        setCouponError("");
                      }}
                      className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-emerald-500 hover:text-emerald-500"
                    >
                      +
                    </button>
                  </div>
                </div>

                <p className="text-xs mt-2 text-gray-400">
                  {program?.kids_discount_percentage
                    ? `Kids get ${program.kids_discount_percentage}% off adult price`
                    : "No kids pricing set"}
                </p>
              </div>

              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600"></span>

                <button
                  onClick={fetchAvailableCoupons}
                  className="text-emerald-600 text-sm font-semibold hover:underline"
                >
                  View Available Coupons
                </button>
              </div>

              <div className="mb-4">
                <TextField
                  label="Have a coupon?"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  error={!!couponError}
                  helperText={couponError || "Enter your coupon code"}
                />
                <Button
                  variant="contained"
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={applyCoupon}
                  fullWidth
                >
                  Apply Coupon
                </Button>

                {couponDiscount > 0 && (
                  <p className="mt-2 text-green-600 font-medium">
                    Coupon applied! You saved ₹
                    {couponDiscount.toLocaleString("en-IN")}
                  </p>
                )}
              </div>

              <Button
                ref={bookNowRef}
                variant="contained"
                fullWidth
                size="large"
                sx={{
                  mt: 1,
                  py: 1.75,
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: "1rem",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                  backgroundColor: "#059669",
                  boxShadow: "0 10px 15px -3px rgba(16,185,129,0.3)",
                  transition: "all .2s",
                  "&:hover": {
                    backgroundColor: "#047857",
                    transform: "translateY(-2px)",
                  },
                }}
                onClick={() => {
                  setTermsModalOpen(true);
                }}
              >
                Book Your Spot
              </Button>

              <div className="mt-4 text-center flex items-center justify-center gap-2 text-xs text-slate-500">
                Secure checkout via Razorpay / Cashfree {/*/ HDFC Bank*/}.
              </div>

              <Divider sx={{ my: 3 }} />

              {/* Inquiry Form Toggle */}
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setShowInquiryForm(!showInquiryForm)}
                startIcon={showInquiryForm ? <CloseIcon /> : <MailIcon />}
                sx={{
                  py: 1.5,
                  borderRadius: "12px",
                  borderWidth: 2,
                  borderColor: "#a7f3d0",
                  color: "#047857",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  fontSize: "0.85rem",
                  "&:hover": {
                    borderWidth: 2,
                    borderColor: "#34d399",
                    backgroundColor: "#f8fafc",
                  },
                }}
              >
                {showInquiryForm ? "Hide Inquiry" : "Inquire / Ask a Question"}
              </Button>

              {showInquiryForm && (
                <div className="mt-4">
                  {submitSuccess && (
                    <p className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm">
                      Thank you! Your details have been recorded.
                    </p>
                  )}

                  <form onSubmit={handleContactSubmit}>
                    <TextField
                      label="Name"
                      variant="outlined"
                      fullWidth
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      error={!!errors.name}
                      helperText={errors.name}
                      margin="normal"
                      size="small"
                    />
                    <TextField
                      label="Phone Number"
                      variant="outlined"
                      fullWidth
                      required
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      error={!!errors.phone}
                      helperText={errors.phone}
                      margin="normal"
                      size="small"
                    />
                    <TextField
                      label="Email"
                      variant="outlined"
                      fullWidth
                      required
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      error={!!errors.email}
                      helperText={errors.email}
                      margin="normal"
                      size="small"
                    />
                    <TextField
                      label="Preferred Travel Date"
                      type="date"
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                      value={travelDate}
                      inputProps={{
                        min: new Date().toISOString().split("T")[0],
                      }}
                      onChange={(e) => setTravelDate(e.target.value)}
                      error={!!errors.travelDate}
                      helperText={errors.travelDate}
                      margin="normal"
                      size="small"
                    />
                    <TextField
                      label="Number of Guests"
                      type="number"
                      fullWidth
                      required
                      inputProps={{ min: 1 }}
                      value={travellerCount}
                      onChange={(e) => setTravellerCount(e.target.value)}
                      error={!!errors.travellerCount}
                      helperText={errors.travellerCount}
                      margin="normal"
                      size="small"
                    />
                    <TextField
                      label="Message"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={3}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      margin="normal"
                      size="small"
                      placeholder="Any questions or special requests?"
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      Send Inquiry
                    </Button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {bookingModalOpen && program && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40">
          <div className="relative bg-white w-full md:max-w-xl max-h-[90vh] rounded-t-2xl md:rounded-2xl shadow-2xl overflow-y-auto">
            {/* Close button */}
            <button
              onClick={closeBookingModal}
              className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none z-10"
            >
              ×
            </button>

            {/* Program header */}
            <div className="px-4 pt-4 pb-3 flex items-start gap-3 border-b border-gray-100">
            <div className="w-14 h-14 rounded-md overflow-hidden flex-shrink-0">
  <SafeImage src={pickImage(program.image)} type="wellness" alt={program.title} compact />
</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-800 line-clamp-2">
                  {program.title}
                </p>
                <p className="text-xs text-gray-600 mt-1">{program.duration}</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="mt-1">
                    <span className="text-base font-bold text-emerald-600">
                      ₹{effectivePricePerPerson.toLocaleString("en-IN")}
                    </span>
                    {selectedOption && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {program?.accommodations?.[selectedRoomIndex]?.type}
                        {selectedOption.occupancy === "shared" &&
                        selectedOption.sharing_type
                          ? ` · ${sharingLabels[selectedOption.sharing_type] || selectedOption.sharing_type}`
                          : selectedOption.occupancy
                            ? ` · ${selectedOption.occupancy.charAt(0).toUpperCase() + selectedOption.occupancy.slice(1)}`
                            : ""}
                      </p>
                    )}
                  </div>
                  {/* {hasDiscount && (
                    <span className="text-xs text-gray-400 line-through">
                      ₹{originalPrice.toLocaleString("en-IN")}
                    </span>
                  )}*/}
                </div>
              </div>
            </div>

            {/* Booking Form */}

            <form
                         onSubmit={(e) => {
                e.preventDefault();
                if (paymentGateway === "cashfree") {
                  handleCashfreePayment();
                } else if (paymentGateway === "hdfc") {
                  handleHdfcPayment();
                } else {
                  handleBookingSubmit(e);
                }
              }}
              className="px-4 pb-4 pt-2 space-y-3"
            >
              <p className="text-sm text-gray-600 mb-2">
                Complete your booking details and we'll confirm your reservation
                shortly.
              </p>

              <TextField
                label="Full Name"
                variant="outlined"
                fullWidth
                required
                value={bookingName}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[A-Za-z\s]*$/.test(value)) {
                    setBookingName(value);
                  }
                }}
                size="small"
              />

              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                required
                type="email"
                value={bookingEmail}
                onChange={(e) => setBookingEmail(e.target.value)}
                size="small"
              />

              <div className="grid grid-cols-4 gap-2">
                <TextField
                  select
                  value="+91"
                  size="small"
                  disabled
                  fullWidth
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="+91">+91</option>
                </TextField>

                <TextField
                  label="Your Phone"
                  variant="outlined"
                  fullWidth
                  required
                  type="tel"
                  size="small"
                  value={bookingPhone}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,10}$/.test(value)) {
                      setBookingPhone(value);
                    }
                  }}
                  inputProps={{ maxLength: 10 }}
                  className="col-span-3"
                />
              </div>

              <div className="space-y-3">
                {program.date_type === "flexible" ? (
                  <TextField
                    label="Travel Date"
                    type="date"
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: new Date().toISOString().split("T")[0] }}
                    value={bookingTravelDate}
                    onChange={(e) => setBookingTravelDate(e.target.value)}
                    size="small"
                  />
                ) : (
                  <TextField
                    label="Selected Retreat Date"
                    fullWidth
                    value={
                      selectedSchedule
                        ? `${new Date(selectedSchedule.startDate).toLocaleDateString("en-IN")} - ${new Date(selectedSchedule.endDate).toLocaleDateString("en-IN")}`
                        : ""
                    }
                    InputProps={{ readOnly: true }}
                    size="small"
                  />
                )}

                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Adults
                      </p>
                      <p className="text-xs text-gray-400">Above 12 Years</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          const next = Math.max(1, Number(travellerCount) - 1);
                          setTravellerCount(next);
                          setBookingTravellerCount(next);
                          if (couponDiscount > 0) {
                            setCouponDiscount(0);
                            setCouponCode("");
                            setCouponError("");
                            alert(
                              "Guest count changed. Please re-apply your coupon in the sidebar.",
                            );
                          }
                        }}
                        disabled={Number(travellerCount) <= 1}
                        className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-emerald-500 hover:text-emerald-500 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        −
                      </button>
                      <span className="w-6 text-center text-sm font-semibold">
                        {String(travellerCount).padStart(2, "0")}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const next = Number(travellerCount) + 1;
                          setTravellerCount(next);
                          setBookingTravellerCount(next);
                          if (couponDiscount > 0) {
                            setCouponDiscount(0);
                            setCouponCode("");
                            setCouponError("");
                            alert(
                              "Guest count changed. Please re-apply your coupon in the sidebar.",
                            );
                          }
                        }}
                        className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-emerald-500 hover:text-emerald-500"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Kids</p>
                      <p className="text-xs text-gray-400">
                        {Number(kidsCount) > 0 && kidsDiscountPct > 0
                          ? `₹${kidsPricePerPerson.toLocaleString("en-IN")}/kid`
                          : "Below 12 yrs"}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setKidsCount(Math.max(0, Number(kidsCount) - 1));
                          if (couponDiscount > 0) {
                            setCouponDiscount(0);
                            setCouponCode("");
                            setCouponError("");
                          }
                        }}
                        disabled={Number(kidsCount) <= 0}
                        className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-emerald-500 hover:text-emerald-500 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        −
                      </button>
                      <span className="w-6 text-center text-sm font-semibold">
                        {String(kidsCount).padStart(2, "0")}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setKidsCount(Number(kidsCount) + 1);
                          if (couponDiscount > 0) {
                            setCouponDiscount(0);
                            setCouponCode("");
                            setCouponError("");
                          }
                        }}
                        className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-emerald-500 hover:text-emerald-500"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <TextField
                label="Special Requests (Optional)"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                size="small"
                value={bookingMessage}
                onChange={(e) => setBookingMessage(e.target.value)}
                placeholder="Any dietary requirements, health concerns, or special requests..."
              />

              <div className="border rounded-lg p-3 bg-gray-50 mb-3">
                <p className="text-sm font-semibold text-gray-800 mb-3">
                  Choose Payment Gateway
                </p>
                     <div className="flex gap-3">
                  <label
                    className={`flex-1 flex items-center gap-2 p-3 border rounded-lg cursor-pointer bg-white ${paymentGateway === "razorpay" ? "border-emerald-600 bg-emerald-50" : "border-gray-200"}`}
                  >
                    <input
                      type="radio"
                      name="gateway"
                      value="razorpay"
                      checked={paymentGateway === "razorpay"}
                      onChange={() => setPaymentGateway("razorpay")}
                    />
                    <span className="text-sm font-medium">Razorpay</span>
                  </label>
                  <label
                    className={`flex-1 flex items-center gap-2 p-3 border rounded-lg cursor-pointer bg-white ${paymentGateway === "cashfree" ? "border-emerald-600 bg-emerald-50" : "border-gray-200"}`}
                  >
                    <input
                      type="radio"
                      name="gateway"
                      value="cashfree"
                      checked={paymentGateway === "cashfree"}
                      onChange={() => setPaymentGateway("cashfree")}
                    />
                    <span className="text-sm font-medium">Cashfree</span>
                  </label>
                {/*}  <label
                    className={`flex-1 flex items-center gap-2 p-3 border rounded-lg cursor-pointer bg-white ${paymentGateway === "hdfc" ? "border-emerald-600 bg-emerald-50" : "border-gray-200"}`}
                  >
                    <input
                      type="radio"
                      name="gateway"
                      value="hdfc"
                      checked={paymentGateway === "hdfc"}
                      onChange={() => setPaymentGateway("hdfc")}
                    />
                    <span className="text-sm font-medium">HDFC Bank</span>
                  </label>*/}
                </div>
              </div>

              <div className="border rounded-lg p-3 bg-gray-50">
                <p className="text-sm font-semibold text-gray-800 mb-3">
                  Choose Payment Option
                </p>

                <label
                  className={`flex items-start gap-3 p-3 border rounded-lg mb-2 bg-white
  ${canPayAdvance ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
                >
                  <input
                    type="radio"
                    name="paymentOption"
                    value="partial"
                    checked={paymentOption === "partial"}
                    onChange={() =>
                      canPayAdvance && setPaymentOption("partial")
                    }
                    disabled={!canPayAdvance}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-gray-900">Pay Advance Now</p>
                    {canPayAdvance ? (
                      <p className="text-sm text-gray-600">
                        Pay ₹{previewAdvanceAmount.toLocaleString("en-IN")} now
                        and ₹{previewRemainingAmount.toLocaleString("en-IN")}{" "}
                        later
                      </p>
                    ) : (
                      <p className="text-sm text-red-500">
                        Not available — trip is within {advanceBookingDays}{" "}
                        days. Full payment required.
                      </p>
                    )}
                  </div>
                </label>

                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer bg-white">
                  <input
                    type="radio"
                    name="paymentOption"
                    value="full"
                    checked={paymentOption === "full"}
                    onChange={() => setPaymentOption("full")}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-gray-900">Pay Full Amount</p>
                    <p className="text-sm text-gray-600">
                      Pay ₹{totalBookingAmount.toLocaleString("en-IN")} now
                    </p>
                  </div>
                </label>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-3">
                <p className="text-xs text-emerald-800 font-semibold mb-2">
                  Booking Summary
                </p>

                {/* 1. Adults — includes program + accommodation/hotel + transport, whichever apply */}
                <div className="flex justify-between text-xs text-gray-700">
                  <span>
                    Adults ({travellerCount} × ₹
                    {effectivePricePerPerson.toLocaleString("en-IN")})
                  </span>
                  <span className="font-semibold">
                    ₹{adultsSubtotal.toLocaleString("en-IN")}
                  </span>
                </div>

                {/* 2. Kids */}
                {Number(kidsCount) > 0 && (
                  <div className="flex justify-between text-xs text-gray-700 mt-1">
                    <span>
                      Kids ({kidsCount} × ₹
                      {kidsPricePerPerson.toLocaleString("en-IN")}
                      {kidsDiscountPct > 0 && ` · ${kidsDiscountPct}% off`})
                    </span>
                    <span className="font-semibold">
                      ₹{kidsSubtotal.toLocaleString("en-IN")}
                    </span>
                  </div>
                )}

                {/* 3. Subtotal */}
                <div className="flex justify-between text-xs text-gray-700 mt-1 pt-1 border-t border-emerald-200">
                  <span>Subtotal</span>
                  <span className="font-semibold">
                    ₹{subtotal.toLocaleString("en-IN")}
                  </span>
                </div>

                {/* 4. Total Discounts */}
                {couponDiscount > 0 && (
                  <div className="flex justify-between text-xs text-green-700 mt-1">
                    <span>Coupon Discount</span>
                    <span className="font-semibold">
                      − ₹{couponDiscount.toLocaleString("en-IN")}
                    </span>
                  </div>
                )}

                {/* 5. Net Package Cost */}
                <div className="flex justify-between text-xs text-gray-800 font-semibold mt-1">
                  <span>Net Package Cost</span>
                  <span>₹{subtotalAfterCoupon.toLocaleString("en-IN")}</span>
                </div>

                {/* 6. GST */}
                <div className="flex justify-between text-xs text-gray-700 mt-1">
                  <span>GST ({stayGstRate}%)</span>
                  <span className="font-semibold">
                    ₹{gstTotal.toLocaleString("en-IN")}
                  </span>
                </div>

                <Divider sx={{ my: 1 }} />

                {/* 7. Net Payable */}
                <div className="flex justify-between text-sm text-gray-800 font-bold">
                  <span>Net Payable</span>
                  <span>₹{totalBookingAmount.toLocaleString("en-IN")}</span>
                </div>

                {/* 8. Pay Now */}
                <div className="flex justify-between text-sm text-emerald-700 font-bold mt-1">
                  <span>Pay Now</span>
                  <span>₹{advanceAmount.toLocaleString("en-IN")}</span>
                </div>

                {/* 9. Pay Later */}
                {paymentOption === "partial" && (
                  <div className="flex justify-between text-sm text-orange-700 mt-1">
                    <span>Pay Later</span>
                    <span>₹{remainingAmount.toLocaleString("en-IN")}</span>
                  </div>
                )}
              </div>

              <Button
              disabled={cashfreeLoading}
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  mt: 1,
                  mb: 1,
                  py: 1.5,
                  background: "linear-gradient(to right, #047857, #14b8a6)",
                  "&:hover": {
                    background: "linear-gradient(to right, #065f46, #0d9488)",
                  },
                }}
              >
                {paymentOption === "partial"
                  ? "Pay Advance & Confirm"
                  : "Pay Full & Confirm"}
              </Button>

              <p className="text-[10px] text-gray-500 text-center mt-2">
                By confirming, you agree to our terms and conditions. Final
                booking confirmation will be sent via email.
              </p>
            </form>
          </div>
        </div>
      )}

      {/* TERMS MODAL - BEFORE Booking Modal */}
 {termsModalOpen && (
  <div className="fixed inset-0 z-[9999] bg-black/70 flex items-end sm:items-center justify-center sm:p-6">
    <div className="bg-white w-full sm:max-w-4xl max-h-[92dvh] sm:max-h-[90vh] flex flex-col shadow-2xl rounded-t-3xl sm:rounded-3xl overflow-hidden">
      <div className="p-4 sm:p-6 border-b border-gray-200 shrink-0">
        <div className="flex justify-between items-center gap-3">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Terms & Conditions</h2>
          <button onClick={() => setTermsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-xl">
            <CloseIcon className="w-6 h-6 text-gray-500" />
          </button>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">Wellness Programs | Required before booking</p>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
        {serviceTerms ? (
          <div
            className="prose prose-sm sm:prose-lg max-w-none break-words [&_img]:max-w-full [&_img]:h-auto [&_table]:block [&_table]:overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: serviceTerms }}
          />
        ) : (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading terms...</p>
          </div>
        )}
      </div>

      <div className="shrink-0 p-4 sm:p-6 border-t bg-gradient-to-r from-emerald-50 to-teal-50 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <label className="flex items-start gap-3 p-3 sm:p-4 bg-white rounded-xl border-2 border-dashed border-emerald-300 hover:border-emerald-500 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={termsAccepted}
            className="mt-1 h-5 w-5 text-emerald-600 border-2 border-gray-300 rounded focus:ring-emerald-500 flex-shrink-0"
            onChange={(e) => setTermsAccepted(e.target.checked)}
          />
          <span className="text-xs sm:text-sm text-gray-700 leading-relaxed">
            I have read and agree to the{" "}
            <strong className="text-emerald-700 font-bold">Terms & Conditions</strong> above
          </span>
        </label>

        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <Button variant="outlined" fullWidth onClick={() => setTermsModalOpen(false)}
            sx={{ textTransform: "none", py: 1.25 }}>
            Close
          </Button>
          <Button
            variant="contained"
            fullWidth
            disabled={!termsAccepted}
            onClick={() => {
              setTermsModalOpen(false);
              const eventId = crypto.randomUUID();
              setCheckoutEventId(eventId);
              trackEvent("InitiateCheckout", {
                content_ids: [program._id],
                content_type: "wellness_package",
                value: effectivePricePerPerson,
                currency: "INR",
                num_items: travellerCount,
              }, eventId);
              openBookingModal();
            }}
            sx={{
              background: "linear-gradient(to right, #047857, #14b8a6)",
              "&:hover": { background: "linear-gradient(to right, #065f46, #0d9488)" },
              "&:disabled": { backgroundColor: "#d1d5db", color: "#9ca3af" },
              textTransform: "none", fontWeight: "bold", py: 1.25,
              boxShadow: "0 4px 12px rgba(4, 120, 87, 0.4)",
            }}
          >
            Accept & Continue
          </Button>
        </div>
      </div>
    </div>
  </div>
)}

{/* Mobile Inquiry Modal */}
{showInquiryForm && (
 <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 lg:hidden">
    <div className="relative bg-white w-full max-h-[90vh] rounded-t-2xl shadow-2xl overflow-y-auto mb-12">
      <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100">
        <h2 className="text-lg font-bold text-gray-900">Send an Inquiry</h2>
        <button
          onClick={() => setShowInquiryForm(false)}
          className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none"
        >
          ×
        </button>
      </div>

      <div className="px-4 pb-6 pt-3">
        {submitSuccess && (
          <p className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm">
            Thank you! Your details have been recorded.
          </p>
        )}

        <form onSubmit={handleContactSubmit}>
          <TextField label="Name" variant="outlined" fullWidth required
            value={name} onChange={(e) => setName(e.target.value)}
            error={!!errors.name} helperText={errors.name} margin="normal" size="small" />
          <TextField label="Phone Number" variant="outlined" fullWidth required type="tel"
            value={phone} onChange={(e) => setPhone(e.target.value)}
            error={!!errors.phone} helperText={errors.phone} margin="normal" size="small" />
          <TextField label="Email" variant="outlined" fullWidth required type="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email} helperText={errors.email} margin="normal" size="small" />
          <TextField label="Preferred Travel Date" type="date" fullWidth required
            InputLabelProps={{ shrink: true }} value={travelDate}
            inputProps={{ min: new Date().toISOString().split("T")[0] }}
            onChange={(e) => setTravelDate(e.target.value)}
            error={!!errors.travelDate} helperText={errors.travelDate} margin="normal" size="small" />
          <TextField label="Number of Guests" type="number" fullWidth required
            inputProps={{ min: 1 }} value={travellerCount}
            onChange={(e) => setTravellerCount(e.target.value)}
            error={!!errors.travellerCount} helperText={errors.travellerCount} margin="normal" size="small" />
          <TextField label="Message" variant="outlined" fullWidth multiline rows={3}
            value={message} onChange={(e) => setMessage(e.target.value)}
            margin="normal" size="small" placeholder="Any questions or special requests?" />
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 2, mb: 1 }}>
            Send Inquiry
          </Button>
        </form>
      </div>
    </div>
  </div>
)}

{changeHotelStop && (
  <ChangeHotelPanel
    stop={changeHotelStop}
    currentSelection={selectedHotels[changeHotelStop.key] || null}  
    allowNoHotel={program.stay_policy !== "mandatory"}
    onSelect={(hotel, room) => {
      setSelectedHotels(prev => ({ ...prev, [changeHotelStop.key]: { hotel, room } }));
      setSelectedRoomIndex(null);
      setSelectedOccupancyIndex(null);
      setStayChoice('hotel');
    }}
    onClearHotel={() => {
      setSelectedHotels(prev => {
        const next = { ...prev };
        delete next[changeHotelStop.key];
        return next;
      });
      setStayChoice(null);
    }}
    onClose={() => setChangeHotelStop(null)}
  />
)}

{showAccommodationPanel && (
  <ChangeAccommodationPanel
    accommodations={program.accommodations}
    currentRoomIndex={selectedRoomIndex}
    currentOccIndex={selectedOccupancyIndex}
    onSelect={(rIdx, oIdx) => {
      setSelectedRoomIndex(rIdx);
      setSelectedOccupancyIndex(oIdx);
      setSelectedHotels({});
      setStayChoice('room');
    }}
    onClose={() => setShowAccommodationPanel(false)}
  />
)}


{showTransportList && Array.isArray(program.transport) && (
  <TransportOptionsPanel
    transport={program.transport}
    selectedTransport={selectedTransportOption}
    totalGuests={travellerCount}
    onSelect={(t) => {
      const idx = program.transport.findIndex(
        (x) => x.vehicle === t.vehicle && x.type === t.type
      );
      setSelectedTransportIndex(idx);
      setTransportAutoSelected(false);
    }}
    onClose={() => setShowTransportList(false)}
  />
)}

      {couponModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end md:items-center justify-center">
          <div className="bg-white w-full md:max-w-md rounded-t-2xl md:rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">Available Coupons</h2>
              <button onClick={() => setCouponModalOpen(false)}>✕</button>
            </div>

            {availableCoupons.length === 0 ? (
              <p className="text-gray-500 text-center">No coupons available</p>
            ) : (
              <div className="space-y-3">
                {availableCoupons.map((coupon) => (
                  <div
                    key={coupon._id}
                    className="border rounded-lg p-4 flex justify-between items-center"
                  >
                    <div>
                      <p className="font-bold text-emerald-700">
                        {coupon.coupon_code}
                      </p>

                      <p className="text-sm text-gray-600">
                        {coupon.discount_type === "percentage"
                          ? `${coupon.coupon_amount}% off`
                          : coupon.apply_to === "per_person"
                            ? `₹${coupon.coupon_amount} off per person`
                            : `₹${coupon.coupon_amount} off`}
                      </p>
                    </div>

                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        setCouponCode(coupon.coupon_code);
                        setCouponModalOpen(false);
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

{/* MOBILE STICKY BOOK NOW BAR */}
{showSticky && (
  <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-10px_20px_rgba(0,0,0,0.05)] lg:hidden">
    <div className="flex items-center justify-between gap-2 w-full min-w-0">
      <div className="flex-shrink-0 min-w-0">
        <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">From</p>
        <p className="text-base font-bold text-emerald-900 truncate">
          ₹{effectivePricePerPerson.toLocaleString("en-IN")}
        </p>
      </div>

      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={() => setShowInquiryForm(true)}
          className="border-2 border-emerald-600 text-emerald-700 font-bold px-3 py-2 rounded-xl text-xs max-w-[110px] truncate"
        >
          Send Inquiry
        </button>
      <button
  onClick={() =>
    document.getElementById("booking-card")?.scrollIntoView({ behavior: "smooth" })
  }
  className="border-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-2 rounded-xl shadow-lg shadow-emerald-500/30 text-xs max-w-[110px] truncate"
>
  Book Now
</button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default WellnessDetail;